import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { reorderByDate } from '../../utils/jobOrdering'
import { sortSegments, validateSegments, writeSegments, segmentRange } from '../../lib/segments'
import SegmentsEditor from './SegmentsEditor'
import { summarizeSand, loadSandStandards } from '../../lib/poolSand'
import { resolveDueOnSite, bidTotalForJob, saveDueOnSite, fmtMoney as fmtMoneyDOS } from '../../lib/dueOnSite'
import { timeAgo } from '../../utils/formatters'
import { JOB_SCOPE_COLUMNS, INSTALL_TYPE_LABELS } from '../../lib/scopeFlags'

import { STATUS_COLORS } from '../../utils/colors'
import { isCancelled } from '../../utils/jobStatus'

const POOLS_JOB_TYPES = [
  { value: 'above_ground', label: 'Above Ground Pool' },
  { value: 'inground_residential', label: 'Inground - Residential' },
  { value: 'inground_commercial', label: 'Inground - Commercial' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'repair', label: 'Repair' },
  { value: 'landscaping', label: 'Landscaping' },
]

const RIVERBEND_JOB_TYPES = [
  { value: 'land_clearing', label: 'Land Clearing' },
  { value: 'mulching', label: 'Mulching' },
  { value: 'stump_grinding', label: 'Stump Grinding' },
  { value: 'site_prep', label: 'Site Preparation' },
  { value: 'brush_removal', label: 'Brush Removal' },
]

// Defend the layout against junk values imported from CSVs (very long strings,
// null, accidental objects). React happily renders any string, but unbounded
// length blows up the modal header — and we can't trust imported data shape.
function truncate(value, max) {
  if (value == null) return ''
  var s = String(value)
  if (s.length <= max) return s
  return s.slice(0, max - 1) + String.fromCodePoint(0x2026)
}

export default function JobCard({ job, onClose, onUpdate, safeMode }) {
  const { profile } = useAuth()
  const [fullJob, setFullJob] = useState(job)
  const [loading, setLoading] = useState(true)
  const [showPayment, setShowPayment] = useState(false)
  const [showInvoice, setShowInvoice] = useState(false)
  const [invoiceSending, setInvoiceSending] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [crewLeaders, setCrewLeaders] = useState([])
  const [jobExpenses, setJobExpenses] = useState([])
  const [timeEntries, setTimeEntries] = useState([])
  const [jobCrew, setJobCrew] = useState([])
  const [myClock, setMyClock] = useState(null)
  const [segments, setSegments] = useState([])
  const [editSegments, setEditSegments] = useState([])
  const [editMultiVisit, setEditMultiVisit] = useState(false)
  const [sandStandards, setSandStandards] = useState([])
  const [showDueEdit, setShowDueEdit] = useState(false)
  const [dueForm, setDueForm] = useState({ amount: '', notes: '', useBidTotal: false })
  const [dueSaving, setDueSaving] = useState(false)

  const openDueEdit = () => {
    const overridden = fullJob.due_on_site != null
    setDueForm({
      amount: overridden ? String(fullJob.due_on_site) : String(bidTotalForJob(fullJob) || ''),
      notes: fullJob.due_on_site_notes || '',
      useBidTotal: !overridden,
    })
    setShowDueEdit(true)
  }

  const saveDueEdit = async () => {
    setDueSaving(true)
    const amount = dueForm.useBidTotal ? null : (dueForm.amount === '' ? null : parseFloat(dueForm.amount))
    if (!dueForm.useBidTotal && (amount == null || isNaN(amount) || amount < 0)) {
      alert('Enter a valid dollar amount, or check "Use bid total".')
      setDueSaving(false)
      return
    }
    const { error } = await saveDueOnSite(job.id, { amount: amount, notes: dueForm.notes, userId: profile?.id })
    if (error) {
      alert('Save failed: ' + error.message)
      setDueSaving(false)
      return
    }
    setFullJob(j => ({ ...j, due_on_site: amount, due_on_site_notes: dueForm.notes || null }))
    setShowDueEdit(false)
    setDueSaving(false)
    if (onUpdate) onUpdate()
  }
  const [bid, setBid] = useState(null)
  const [bidLines, setBidLines] = useState([])
  const [storeExtras, setStoreExtras] = useState(null) // { extras, custom_items, half_sunk, tear_down, notes }
  const [bidLoading, setBidLoading] = useState(false)
  const [showComplete, setShowComplete] = useState(false)
  const [completeForm, setCompleteForm] = useState({})
  const [completing, setCompleting] = useState(false)
  const [completedByName, setCompletedByName] = useState(null)
  const [showSnapshot, setShowSnapshot] = useState(false)
  const [showSyncLog, setShowSyncLog] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [autoSyncSaving, setAutoSyncSaving] = useState(false)
  const [showLinkBid, setShowLinkBid] = useState(false)
  const [linkableBids, setLinkableBids] = useState([])
  const [linkBidSearch, setLinkBidSearch] = useState('')
  const [linkingBid, setLinkingBid] = useState(false)
  const [creatingBid, setCreatingBid] = useState(false)
  useEffect(function() {
    if (fullJob.business_id === 'pools') loadSandStandards().then(setSandStandards)
  }, [fullJob.business_id])
  const role = profile?.role
  const canEditDueOnSite = role === 'owner' || role === 'office_staff' || role === 'project_manager'
  const canViewDueOnSite = canEditDueOnSite || role === 'crew_leader'
  const canEdit = role === 'owner' || role === 'office_staff' || role === 'project_manager' || role === 'crew_leader'
  const canClock = fullJob.business_id === 'pools' && ['crew_leader', 'project_manager', 'owner'].includes(role)
  const canETA = ['owner', 'office_staff', 'project_manager', 'crew_leader'].includes(role)
  const canSeePricing = ['owner', 'office_staff', 'project_manager', 'crew_leader'].includes(role)
  // Owner/office/PM see dollar amounts on completed jobs. Crew leaders can
  // mark complete but the panel shows "Paid" without specifics.
  const canSeeCollectedAmount = ['owner', 'office_staff', 'project_manager'].includes(role)
  const canMarkComplete = ['owner', 'office_staff', 'project_manager', 'crew_leader'].includes(role)
  const canEditCollected = role === 'owner'
  var [showETA, setShowETA] = useState(false)
  var [etaData, setEtaData] = useState({ from: '11260 Dunham Rd, Hartland MI 48353', drive: null, lunch: false, message: '', loading: false, sending: false })
  const isAboveGround = fullJob.business_id === 'pools' && fullJob.job_type === 'above_ground'
  const isMulching = fullJob.business_id === 'riverbend' && fullJob.job_type === 'mulching'
  const showFinancials = isAboveGround || isMulching

  const sendJobInvoice = async (method, contact) => {
    if (!contact) { alert('Enter ' + (method === 'email' ? 'email' : 'phone')); return }
    setInvoiceSending(true)
    try {
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF()
      const bizName = fullJob.business_id === 'pools' ? 'CP Quality Pools' : 'Riverbend Contracting Co.'
      const amt = Number(fullJob.contract_value || 0)
      if (fullJob.business_id === 'pools') {
        const { CP_QUALITY_LOGO } = await import('../../assets/cpQualityLogo')
        try { doc.addImage(CP_QUALITY_LOGO, 'PNG', 20, 8, 25, 25, 'cpq', 'FAST') } catch (e) {}
      }
      doc.setFontSize(24); doc.setFont('helvetica', 'bold'); doc.text(bizName, 105, 22, { align: 'center' })
      doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 100, 100)
      doc.text('Pietila Group | (810) 923-6229 | pietilagroup.com', 105, 29, { align: 'center' })
      doc.setDrawColor(180, 140, 60); doc.setLineWidth(0.8); doc.line(20, 35, 190, 35)
      doc.setTextColor(0, 0, 0); doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.text('INVOICE', 20, 48)
      doc.setFontSize(10); doc.setFont('helvetica', 'normal')
      doc.text(new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }), 190, 48, { align: 'right' })
      let y = 60
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.text('BILL TO:', 20, y); y += 5
      doc.setFontSize(11); doc.text(fullJob.client_name || '', 20, y); y += 5
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60)
      if (fullJob.site_address) { doc.text(fullJob.site_address, 20, y); y += 5 }
      if (fullJob.client_phone) { doc.text(fullJob.client_phone, 20, y); y += 5 }
      y += 8
      if (fullJob.title || fullJob.job_name) { doc.setFont('helvetica', 'italic'); doc.setTextColor(60, 100, 160); doc.text('Project: ' + (fullJob.title || fullJob.job_name), 20, y); y += 8 }
      doc.setFillColor(30, 40, 60); doc.rect(20, y, 170, 9, 'F')
      doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(9)
      doc.text('DESCRIPTION', 24, y + 6); doc.text('AMOUNT', 186, y + 6, { align: 'right' }); y += 13
      doc.setTextColor(30, 30, 30); doc.setFont('helvetica', 'normal'); doc.setFontSize(10)
      const desc = (fullJob.job_type || '').replace(/_/g, ' ').toUpperCase() + ' - ' + (fullJob.title || fullJob.client_name || 'Service')
      doc.text(desc, 24, y); doc.text('$' + amt.toLocaleString('en-US', { minimumFractionDigits: 2 }), 186, y, { align: 'right' }); y += 12
      doc.setDrawColor(180, 140, 60); doc.setLineWidth(0.6); doc.line(110, y, 190, y); y += 8
      doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0)
      doc.text('TOTAL:', 110, y); doc.text('$' + amt.toLocaleString('en-US', { minimumFractionDigits: 2 }), 186, y, { align: 'right' }); y += 8
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0)
      doc.text('STATUS: PAID', 110, y)
      doc.setFontSize(7); doc.setTextColor(150, 150, 150); doc.text(bizName + ' | (810) 923-6229', 105, 287, { align: 'center' })
      const blob = doc.output('blob')
      const fileName = 'invoice_job_' + fullJob.id + '_' + Date.now() + '.pdf'
      const { error: upErr } = await supabase.storage.from('quotes').upload(fileName, blob, { contentType: 'application/pdf' })
      if (upErr) { alert('Upload failed: ' + upErr.message); setInvoiceSending(false); return }
      const { data: urlData } = supabase.storage.from('quotes').getPublicUrl(fileName)
      const pdfUrl = urlData.publicUrl
      if (method === 'email') {
        const subj = 'Invoice from ' + bizName + ' - ' + (fullJob.title || fullJob.client_name || '')
        const body = 'Hello ' + (fullJob.client_name || '') + ',\n\nPlease find your paid invoice below:\n\nAmount: $' + amt.toLocaleString('en-US', { minimumFractionDigits: 2 }) + '\n\nView / Download Invoice:\n' + pdfUrl + '\n\nThank you for your business!\n\n' + bizName + '\n(810) 923-6229'
        const resp = await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: contact, subject: subj, body: body, business_id: fullJob.business_id }) })
        if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.error || 'Email failed') }
        alert('Paid invoice emailed to ' + contact + '!')
      } else {
        const msg = 'Invoice from ' + bizName + '\nAmount: $' + amt.toLocaleString('en-US', { minimumFractionDigits: 2 }) + '\nStatus: PAID\n\nView invoice:\n' + pdfUrl + '\n\n' + bizName + ' | (810) 923-6229'
        const resp = await fetch('/api/send-sms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: '+1' + contact.replace(/\D/g, ''), message: msg, business_id: fullJob.business_id }) })
        if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.error || 'SMS failed') }
        alert('Paid invoice sent via text!')
      }
      setShowInvoice(false)
    } catch (err) { alert('Failed: ' + err.message) }
    setInvoiceSending(false)
  }

  const bizColor = fullJob.business_id === 'pools' ? '#b48c3c' : '#4a8c6a'
  const bizLabel = fullJob.business_id === 'pools' ? 'CP QUALITY POOLS' : 'RIVERBEND CLEARING'
  const jobTypes = fullJob.business_id === 'pools' ? POOLS_JOB_TYPES : RIVERBEND_JOB_TYPES

  useEffect(() => {
    const fetchFullJob = async () => {
      setLoading(true)
      try {
        var [jobRes, timeRes, segRes] = await Promise.all([
          supabase.from('jobs').select('*, profiles!jobs_crew_leader_id_fkey(full_name, phone)').eq('id', job.id).single(),
          supabase.from('job_time_entries').select('*, profiles(full_name)').eq('job_id', job.id).order('clock_in', { ascending: false }),
          supabase.from('job_segments').select('*').eq('job_id', job.id).order('segment_number', { ascending: true })
        ])
        if (jobRes.error) console.warn('[JobCard] job fetch failed', jobRes.error)
        if (timeRes.error) console.warn('[JobCard] job_time_entries fetch failed', timeRes.error)
        if (segRes.error) console.warn('[JobCard] job_segments fetch failed', segRes.error)
        if (jobRes.data) { setFullJob(jobRes.data); setEditForm(jobRes.data); setEditMultiVisit(!!jobRes.data.has_segments) }
        setTimeEntries(timeRes.data || [])
        var myOpen = (timeRes.data || []).find(function(e) { return e.user_id === profile?.id && !e.clock_out })
        setMyClock(myOpen || null)
        var segs = sortSegments(segRes.data || [])
        setSegments(segs)
        setEditSegments(segs.length > 0 ? segs : [{ segment_number: 1, start_date: jobRes.data?.start_date || '', end_date: jobRes.data?.end_date || jobRes.data?.start_date || '', description: '', crew_size_needed: '' }])
      } catch (err) {
        console.warn('[JobCard] fetchFullJob threw', err)
        setTimeEntries([])
        setMyClock(null)
        setSegments([])
      }
      setLoading(false)
    }
    fetchFullJob()
  }, [job.id, profile?.id])
  useEffect(function() {
    if (!job || !job.id) return
    supabase.from('expenses').select('*').eq('job_id', job.id).order('expense_date', { ascending: false })
      .then(function(res) { if (res.data) setJobExpenses(res.data) })
  }, [job?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load scheduled crew from worker_schedule for this job's date window
  useEffect(function() {
    if (!job || !job.id || !fullJob?.start_date) return
    var endDate = fullJob.end_date || fullJob.start_date
    supabase.from('worker_schedule')
      .select('*, profiles:user_id(full_name, role)')
      .eq('job_id', job.id)
      .gte('schedule_date', fullJob.start_date)
      .lte('schedule_date', endDate)
      .then(function(res) { if (res.data) setJobCrew(res.data) })
  }, [job?.id, fullJob?.start_date, fullJob?.end_date]) // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const fetchLeaders = async () => {
      const { data } = await supabase.from('profiles').select('id, full_name').in('role', ['project_manager', 'crew_leader', 'owner']).contains('business_ids', [fullJob.business_id])
      setCrewLeaders(data || [])
    }
    fetchLeaders()
  }, [fullJob.business_id])

  // Resolve completed_by uuid → display name when needed.
  useEffect(function() {
    if (!fullJob.completed_by) { setCompletedByName(null); return }
    supabase.from('profiles').select('full_name').eq('id', fullJob.completed_by).maybeSingle()
      .then(function(r) { setCompletedByName(r.data ? r.data.full_name : null) })
  }, [fullJob.completed_by])

  // Open the Mark Complete modal pre-filled with the bid total (or
  // contract_value as fallback). Pre-fills from existing values when
  // backfilling a previously-completed job.
  var openCompleteModal = function() {
    var defaultAmount = fullJob.amount_collected != null
      ? fullJob.amount_collected
      : (bid && bid.total != null ? bid.total : fullJob.contract_value)
    setCompleteForm({
      amount_collected: defaultAmount != null ? String(defaultAmount) : '',
      payment_method: fullJob.payment_method || 'Cash',
      payment_notes: fullJob.payment_notes || '',
    })
    setShowComplete(true)
  }

  var handleMarkComplete = async function() {
    if (completing) return
    var rawAmount = (completeForm.amount_collected || '').toString().trim()
    var amount = rawAmount === '' ? null : parseFloat(rawAmount)
    if (rawAmount !== '' && (isNaN(amount) || amount < 0)) {
      alert('Enter a valid collected amount (or leave blank).')
      return
    }
    setCompleting(true)
    var nowIso = new Date().toISOString()
    var alreadyComplete = fullJob.status === 'completed'
    var patch = {
      amount_collected: amount,
      payment_method: completeForm.payment_method || null,
      payment_notes: completeForm.payment_notes || null,
    }
    if (!alreadyComplete) {
      patch.status = 'completed'
      patch.completed_at = nowIso
      patch.completed_by = profile?.id
    }
    var { data, error } = await supabase.from('jobs').update(patch).eq('id', fullJob.id).select().single()
    if (error) {
      alert('Could not save: ' + error.message)
      setCompleting(false)
      return
    }
    setFullJob(data || Object.assign({}, fullJob, patch))
    setShowComplete(false)
    setCompleting(false)
    if (onUpdate) onUpdate(data)

    // Notify owner if a non-owner completed (or backfilled) the job.
    if (!alreadyComplete && role !== 'owner') {
      try {
        await supabase.from('notifications').insert({
          user_id: 'b85cd872-6750-494a-960e-7b8d32498154',
          type: 'job_completed',
          title: 'Job Completed',
          message: (profile?.full_name || 'A crew member') + ' marked "' + (fullJob.job_name || fullJob.client_name || 'a job') + '" complete' + (amount != null ? ' \u2014 collected $' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''),
          related_id: fullJob.id,
          link: '/jobs?job=' + fullJob.id,
          business_id: fullJob.business_id,
        })
      } catch (e) {
        console.warn('[JobCard] complete notification failed', e)
      }
    }
  }

  const runManualSync = async function() {
    if (syncing || !fullJob.bid_id) return
    setSyncing(true)
    try {
      var resp = await fetch('/api/sync-jobs-from-bids?job_id=' + fullJob.id, { method: 'POST' })
      var data = await resp.json().catch(function() { return {} })
      var msg = ''
      if (data.synced) msg = 'Synced — pulled latest changes from bid.'
      else if (data.skipped) msg = 'No changes — bid is already up to date.'
      else if (data.errors && data.errors.length) msg = 'Sync error: ' + data.errors[0].error
      else msg = 'Sync ran.'
      alert(msg)
      var { data: refreshed } = await supabase.from('jobs').select('*, profiles!jobs_crew_leader_id_fkey(full_name, phone)').eq('id', fullJob.id).single()
      if (refreshed) {
        setFullJob(refreshed)
        if (onUpdate) onUpdate(refreshed)
      }
    } catch (err) {
      alert('Sync failed: ' + err.message)
    }
    setSyncing(false)
  }

  const toggleAutoSync = async function() {
    if (autoSyncSaving) return
    setAutoSyncSaving(true)
    var next = !(fullJob.auto_sync_from_bid !== false)
    var { error } = await supabase.from('jobs').update({ auto_sync_from_bid: next }).eq('id', fullJob.id)
    if (error) {
      alert('Could not save: ' + error.message)
    } else {
      setFullJob(function(j) { return Object.assign({}, j, { auto_sync_from_bid: next }) })
    }
    setAutoSyncSaving(false)
  }

  const fetchLinkableBids = async function() {
    var biz = fullJob.business_id || null
    var q = supabase.from('bids').select('id, job_name, client_name, site_address, total, status, business_id, created_at').is('job_id', null).order('created_at', { ascending: false }).limit(100)
    if (biz) q = q.eq('business_id', biz)
    var { data } = await q
    setLinkableBids(data || [])
  }

  const linkExistingBid = async function(bidId) {
    if (linkingBid || !bidId) return
    setLinkingBid(true)
    try {
      var { error: jobErr } = await supabase.from('jobs').update({ bid_id: bidId }).eq('id', fullJob.id)
      if (jobErr) throw jobErr
      await supabase.from('bids').update({ job_id: fullJob.id }).eq('id', bidId)
      setFullJob(function(j) { return Object.assign({}, j, { bid_id: bidId }) })
      setShowLinkBid(false)
      if (onUpdate) onUpdate()
    } catch (err) {
      alert('Could not link bid: ' + err.message)
    }
    setLinkingBid(false)
  }

  const createBidFromJob = async function() {
    if (creatingBid) return
    if (!window.confirm('Create a draft bid pre-filled from this job? It will appear in the Bids tab so you can add line items.')) return
    setCreatingBid(true)
    try {
      var { data: newBid, error } = await supabase.from('bids').insert({
        business_id: fullJob.business_id || 'pools',
        bid_type: fullJob.job_type || null,
        status: 'draft',
        job_name: fullJob.title || fullJob.client_name || null,
        client_name: fullJob.client_name || null,
        client_phone: fullJob.client_phone || null,
        client_email: fullJob.client_email || null,
        site_address: fullJob.site_address || null,
        site_city: fullJob.site_city || null,
        site_state: fullJob.site_state || null,
        site_zip: fullJob.site_zip || null,
        total: fullJob.contract_value != null ? Number(fullJob.contract_value) : null,
        notes: fullJob.notes || null,
        job_id: fullJob.id,
        created_by: profile?.id || null,
      }).select('id').single()
      if (error || !newBid) throw error || new Error('Insert returned no row')
      await supabase.from('jobs').update({ bid_id: newBid.id }).eq('id', fullJob.id)
      setFullJob(function(j) { return Object.assign({}, j, { bid_id: newBid.id }) })
      if (onUpdate) onUpdate()
      window.location.assign('/bids?bid=' + newBid.id)
    } catch (err) {
      alert('Could not create bid: ' + (err.message || 'unknown error'))
    }
    setCreatingBid(false)
  }

  // Load the linked bid + bid_lines + any store_request extras routed through
  // the same bid. Jobs without a bid_id fall through to the "no bid linked"
  // placeholder in the render path. The whole block is in try/catch so a
  // Supabase failure here can't unmount the crew leader sections below.
  useEffect(function() {
    if (!fullJob.bid_id) { setBid(null); setBidLines([]); setStoreExtras(null); return }
    setBidLoading(true)
    ;(async function() {
      try {
        var [bidRes, linesRes, reqRes] = await Promise.all([
          supabase.from('bids').select('id, job_name, bid_type, total, subtotal, notes, disclaimers, updated_at, quote_status, signer_name, signed_at').eq('id', fullJob.bid_id).maybeSingle(),
          supabase.from('bid_lines').select('id, description, quantity, unit, unit_cost, total_cost, category, sort_order').eq('bid_id', fullJob.bid_id).order('sort_order', { ascending: true }),
          supabase.from('store_requests').select('extras, custom_items, half_sunk, tear_down, notes, pool_size, pool_type').eq('bid_id', fullJob.bid_id).maybeSingle(),
        ])
        if (bidRes.error) console.warn('[JobCard] bid fetch failed', bidRes.error)
        if (linesRes.error) console.warn('[JobCard] bid_lines fetch failed', linesRes.error)
        if (reqRes.error) console.warn('[JobCard] store_requests fetch failed', reqRes.error)
        setBid(bidRes.data || null)
        setBidLines(linesRes.data || [])
        // Only surface store extras if at least one flag or the extras/custom_items has content.
        var r = reqRes.data
        var hasExtras = r && (r.half_sunk || r.tear_down || (Array.isArray(r.extras) && r.extras.length > 0) || (Array.isArray(r.custom_items) && r.custom_items.length > 0))
        setStoreExtras(hasExtras ? r : null)
      } catch (err) {
        console.warn('[JobCard] bid load threw', err)
        setBid(null); setBidLines([]); setStoreExtras(null)
      }
      setBidLoading(false)
    })()
  }, [fullJob.bid_id])

  const setEdit = (k, v) => setEditForm(f => ({ ...f, [k]: v }))

  const isRiverbend = fullJob.business_id === 'riverbend'
  const useSegEdit = isRiverbend && editMultiVisit
  const segEditValidation = useSegEdit ? validateSegments(editSegments) : { startError: null, overlapErrors: [] }

  const handleSaveEdit = async () => {
    var nextStart = editForm.start_date
    var nextEnd = editForm.end_date
    if (useSegEdit) {
      if (segEditValidation.startError) { alert(segEditValidation.startError); return }
      if (segEditValidation.overlapErrors.length > 0) { alert('Segments overlap or are out of order.'); return }
      const range = segmentRange(editSegments)
      nextStart = range.start_date
      nextEnd = range.end_date
    }
    setSaving(true)
    const { error } = await supabase.from('jobs').update({
      title: editForm.title,
      client_name: editForm.client_name,
      client_phone: editForm.client_phone,
      client_email: editForm.client_email,
      site_address: editForm.site_address,
      site_city: editForm.site_city,
      site_state: editForm.site_state,
      contract_value: editForm.contract_value ? parseFloat(editForm.contract_value) : null,
      start_date: nextStart,
      end_date: nextEnd,
      crew_leader_id: editForm.crew_leader_id || null,
      job_type: editForm.job_type,
      notes: editForm.notes,
      pool_dimensions: editForm.pool_dimensions,
      dig_specs: editForm.dig_specs,
      utility_locate_status: editForm.utility_locate_status,
      acreage: editForm.acreage ? parseFloat(editForm.acreage) : null,
      debris_disposal: editForm.debris_disposal,
      site_access_notes: editForm.site_access_notes,
      equipment_assigned: editForm.equipment_assigned,
      water_truck_company: editForm.water_truck_company,
      water_truck_time: editForm.water_truck_time,
      sand_delivery_company: editForm.sand_delivery_company,
      sand_delivery_time: editForm.sand_delivery_time,
      sand_delivery: editForm.sand_delivery || false,
      dirt_haul_away: editForm.dirt_haul_away || false,
      dirt_haul_notes: editForm.dirt_haul_notes,
      has_heater: editForm.has_heater || false,
      has_main_drain: editForm.has_main_drain || false,
      has_deep_end: editForm.has_deep_end || false,
      has_deck: editForm.has_deck || false,
      has_segments: useSegEdit,
      sand_required: !!editForm.sand_required,
      sand_yards: editForm.sand_yards != null && editForm.sand_yards !== '' ? parseFloat(editForm.sand_yards) : null,
      sand_type: editForm.sand_type || 'mason sand',
      is_replacement: !!editForm.is_replacement,
      installer_provides_sand: !!editForm.installer_provides_sand,
      sand_notes: editForm.sand_notes || null,
    }).eq('id', job.id)
    if (!error) {
      if (isRiverbend) {
        if (useSegEdit) {
          const seg = await writeSegments(job.id, editSegments)
          if (seg.error) { alert('Segments save failed: ' + seg.error.message); setSaving(false); return }
          setSegments(sortSegments(seg.data || []))
        } else if (fullJob.has_segments) {
          // Switched OFF: drop all segments.
          await supabase.from('job_segments').delete().eq('job_id', job.id)
          setSegments([])
        }
      }
      const dateChanged = nextStart && nextStart !== fullJob.start_date
      const stillActive = fullJob.status !== 'completed' && fullJob.status !== 'cancelled'
      if (dateChanged && stillActive) {
        try { await reorderByDate(job.id, nextStart, fullJob.business_id) } catch (e) { console.error(e) }
      }
      setFullJob(Object.assign({}, editForm, { start_date: nextStart, end_date: nextEnd, has_segments: useSegEdit }))
      setEditing(false)
      if (onUpdate) onUpdate()
    }
    setSaving(false)
  }

  const markSegmentStatus = async (segmentId, newStatus) => {
    const patch = { status: newStatus }
    if (newStatus === 'completed') patch.completed_at = new Date().toISOString()
    if (newStatus !== 'completed') patch.completed_at = null
    const { error } = await supabase.from('job_segments').update(patch).eq('id', segmentId)
    if (error) { alert('Failed to update segment: ' + error.message); return }
    const { data } = await supabase.from('job_segments').select('*').eq('job_id', job.id).order('segment_number', { ascending: true })
    const next = sortSegments(data || [])
    setSegments(next)
    // If all segments completed, mark the overall job completed.
    // A cancelled job is never auto-revived by segment progress — finishing the
    // paperwork on a job that was called off must not turn it back into revenue.
    if (newStatus === 'completed' && next.length > 0 && next.every(s => s.status === 'completed')
        && fullJob.status !== 'completed' && !isCancelled(fullJob)) {
      var nowIso = new Date().toISOString()
      await supabase.from('jobs').update({ status: 'completed', completed_at: nowIso, completed_by: profile?.id }).eq('id', job.id)
      setFullJob(j => ({ ...j, status: 'completed', completed_at: nowIso, completed_by: profile?.id }))
      if (onUpdate) onUpdate()
    }
  }

  var [confirmEmail, setConfirmEmail] = useState(null)
  var [emailSending, setEmailSending] = useState(false)

  var openConfirmEmail = function() {
    var firstName = (fullJob.client_name || 'there').split(' ')[0]
    var bizName = fullJob.business_id === 'pools' ? 'CP Quality Pools' : 'Riverbend Clearing Co.'
    var dateStr = fullJob.start_date ? new Date(fullJob.start_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'your scheduled date'
    var msg = 'Hi ' + firstName + ',\n\nThank you for choosing ' + bizName + '! We\'re writing to confirm that your project has been scheduled to begin on ' + dateStr + '.'
    if (fullJob.site_address) msg += ' We have your site address listed as ' + fullJob.site_address + '.'
    msg += '\n\nBecause weather plays a big role in our scheduling, we will call you before we come out to confirm. If you have any questions in the meantime, feel free to reach us at (810) 923-6229.'
    var isInstall = fullJob.job_type && (fullJob.job_type.includes('install') || fullJob.job_type === 'above_ground') && !fullJob.job_type.includes('liner')
    if (fullJob.business_id === 'pools' && isInstall) msg += '\n\nPlease note: you will need to have mason sand delivered to the site prior to installation. If you need recommendations for a sand supplier, just let us know!'
    msg += '\n\nWe look forward to working with you!\n\n' + bizName + '\n(810) 923-6229\npietilagroup.com'
    setConfirmEmail({ subject: 'Your project is scheduled! - ' + bizName, body: msg, to: fullJob.client_email, business_id: fullJob.business_id })
  }

  var sendConfirmEmail = async function() {
    if (!confirmEmail) return
    setEmailSending(true)
    try {
      var resp = await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: confirmEmail.to, subject: confirmEmail.subject, body: confirmEmail.body, business_id: confirmEmail.business_id }) })
      var result = await resp.json()
      if (result.success) { alert('Confirmation sent to ' + (fullJob.client_name || confirmEmail.to) + '!') }
      else { alert('Send failed: ' + (result.error || 'Unknown error')) }
    } catch (err) { alert('Send failed: ' + err.message) }
    setEmailSending(false)
    setConfirmEmail(null)
  }

  var handleClockIn = async function() {
    var now = new Date().toISOString()
    var { data } = await supabase.from('job_time_entries').insert({ job_id: job.id, user_id: profile.id, clock_in: now }).select('*, profiles(full_name)').single()
    if (data) { setMyClock(data); setTimeEntries(function(prev) { return [data].concat(prev) }) }
    // Auto-match expected time from standards if not set
    if (!fullJob.estimated_hours && fullJob.pool_dimensions) {
      var { data: std } = await supabase.from('pool_time_standards').select('expected_hours').eq('job_type', fullJob.job_type || 'install_aboveground').ilike('pool_size', '%' + (fullJob.pool_dimensions || '') + '%').limit(1).single()
      if (std && std.expected_hours) { await supabase.from('jobs').update({ estimated_hours: std.expected_hours }).eq('id', job.id); setFullJob(function(j) { return Object.assign({}, j, { estimated_hours: std.expected_hours }) }) }
    }
    if (!fullJob.clock_in) { await supabase.from('jobs').update({ clock_in: now, time_status: 'in_progress' }).eq('id', job.id) }
  }

  var handleClockOut = async function() {
    if (!myClock) return
    var now = new Date().toISOString()
    var hours = (new Date(now) - new Date(myClock.clock_in)) / 3600000
    await supabase.from('job_time_entries').update({ clock_out: now, hours_worked: Math.round(hours * 100) / 100 }).eq('id', myClock.id)
    setMyClock(null)
    setTimeEntries(function(prev) { return prev.map(function(e) { return e.id === myClock.id ? Object.assign({}, e, { clock_out: now, hours_worked: Math.round(hours * 100) / 100 }) : e }) })
    // If all entries clocked out, update job
    var remaining = timeEntries.filter(function(e) { return !e.clock_out && e.id !== myClock.id })
    if (remaining.length === 0) { await supabase.from('jobs').update({ clock_out: now, time_status: 'completed' }).eq('id', job.id) }
  }

  var openETA = async function() {
    setShowETA(true)
    setEtaData(function(d) { return Object.assign({}, d, { loading: true }) })

    var fromAddr = '11260 Dunham Rd, Hartland MI 48353'
    var fromLabel = 'Shop (11260 Dunham Rd)'

    // Step 1: Get user's current GPS location
    var gpsResult = await new Promise(function(resolve) {
      if (!navigator.geolocation) { resolve(null); return }
      navigator.geolocation.getCurrentPosition(
        function(pos) { resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }) },
        function() { resolve(null) },
        { enableHighAccuracy: true, timeout: 10000 }
      )
    })

    if (gpsResult) {
      fromAddr = gpsResult.lat + ',' + gpsResult.lng
      fromLabel = 'Your current location'
    } else {
      // Fallback: shop address
      fromAddr = '42.6320,-83.7563'
      fromLabel = 'Shop (11260 Dunham Rd)'
    }

    // Step 2: Call drive time API with origin/destination
    var driveMin = null
    var driveText = null
    if (fullJob.site_address) {
      try {
        var resp = await fetch('/api/drive-times', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ origin: fromAddr, destination: fullJob.site_address }) })
        var data = await resp.json()
        if (data.success && data.duration_minutes) {
          driveMin = data.duration_minutes
          driveText = data.duration_text || (driveMin + ' mins')
        }
      } catch (e) {} // eslint-disable-line no-unused-vars
    }

    // Step 3: Calculate actual arrival time = now + drive time
    var now = new Date()
    var totalMinutes = driveMin || 0
    var eta = driveMin ? new Date(now.getTime() + totalMinutes * 60000) : null
    var etaStr = eta ? eta.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'TBD'

    // Step 4: Build message with ARRIVAL time
    var firstName = (fullJob.client_name || 'there').split(' ')[0]
    var bizName = fullJob.business_id === 'pools' ? 'CP Quality Pools' : 'Riverbend Clearing Co.'
    var msg = 'Hi ' + firstName + ', this is ' + bizName + '. We\'re on our way to your property and expect to arrive around ' + etaStr + '. We\'ll give you a call when we\'re close. Thanks!'
    setEtaData({ from: fromAddr, fromLabel: fromLabel, drive: driveMin, driveText: driveText, lunch: false, message: msg, etaStr: etaStr, loading: false, sending: false, bizName: bizName, firstName: firstName })
  }

  var sendETA = async function(via) {
    setEtaData(function(d) { return Object.assign({}, d, { sending: true }) })
    try {
      if (via === 'sms' && fullJob.client_phone) {
        await fetch('/api/send-sms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: fullJob.client_phone, message: etaData.message, business_id: fullJob.business_id }) })
      }
      if (via === 'email' && fullJob.client_email) {
        await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: fullJob.client_email, subject: 'On our way! - ' + etaData.bizName, body: etaData.message, business_id: fullJob.business_id }) })
      }
      var etaLogRes = await supabase.from('eta_messages').insert({ job_id: job.id, sent_by: profile.id, client_name: fullJob.client_name, client_phone: fullJob.client_phone, client_email: fullJob.client_email, sent_via: via, message: etaData.message, drive_minutes: etaData.drive, lunch_stop: etaData.lunch, eta_time: etaData.etaStr, from_address: etaData.from, to_address: fullJob.site_address })
      if (etaLogRes.error) console.warn('eta_messages log failed', etaLogRes.error)
      alert('ETA sent via ' + via.toUpperCase() + '!')
      setShowETA(false)
    } catch (err) { alert('Failed: ' + err.message) }
    setEtaData(function(d) { return Object.assign({}, d, { sending: false }) })
  }

  const handleStatusChange = async (newStatus) => {
    const patch = { status: newStatus }
    // Leaving 'completed' must clear the completion stamp, or the job keeps
    // reading as finished to every query that keys off completed_at.
    if (newStatus !== 'completed' && fullJob.completed_at) {
      patch.completed_at = null
      patch.completed_by = null
    }
    if (newStatus === 'cancelled') {
      if (!window.confirm('Cancel this job? It will be removed from the active list, the schedule board, and the invoiceable pool.')) return
    }
    await supabase.from('jobs').update(patch).eq('id', job.id)
    // Cancelling releases the linked bid back to draft, matching the Bids page
    // cancel flow. Without this the bid stays 'awarded' and remains invoiceable.
    if (newStatus === 'cancelled' && fullJob.bid_id) {
      await supabase.from('bids').update({ job_id: null, status: 'draft' }).eq('id', fullJob.bid_id)
    }
    // Delete Google Calendar event only when cancelled (completed jobs stay on calendar)
    if (newStatus === 'cancelled' && fullJob.google_event_id) {
      try {
        var { data: tokenRow } = await supabase.from('google_tokens').select('user_id').limit(1).single()
        if (tokenRow) {
          fetch('/api/google-sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: tokenRow.user_id, action: 'delete', google_event_id: fullJob.google_event_id }) }).catch(function() {}) // eslint-disable-line no-unused-vars
        }
      } catch (e) { /* ignore sync errors */ } // eslint-disable-line no-unused-vars
    }
    setFullJob(j => ({ ...j, ...patch }))
    if (onUpdate) onUpdate()
  }

  const openMaps = () => {
    const address = (fullJob.site_address || '') + ', ' + (fullJob.site_city || '') + ', ' + (fullJob.site_state || '')
    window.open('https://maps.google.com/?q=' + encodeURIComponent(address), '_blank')
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={e => e.stopPropagation()}>
        <div style={{ ...styles.cardTop, borderTop: '4px solid ' + bizColor }}>
          <div style={styles.cardHeader}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={styles.jobNumber}>{fullJob.job_number || ''}</div>
              <div style={styles.jobTitle} title={String(fullJob.title || fullJob.client_name || '')}>{truncate(fullJob.title || fullJob.client_name, 60)}</div>
              <div style={{ ...styles.bizLabel, color: bizColor }}>{bizLabel}</div>
            </div>
            <div style={styles.headerRight}>
              <div style={{ ...styles.statusBadge, backgroundColor: (STATUS_COLORS[fullJob.status] || '#888') + '20', color: STATUS_COLORS[fullJob.status] || '#888', borderColor: (STATUS_COLORS[fullJob.status] || '#888') + '40' }}>
                {fullJob.status?.toUpperCase()}
              </div>
              {canEdit && !editing && <button onClick={() => setEditing(true)} style={styles.editBtn}>EDIT</button>}
              {editing && <button onClick={() => setEditing(false)} style={styles.editBtn}>CANCEL</button>}
              <button onClick={onClose} style={styles.closeBtn}>{String.fromCodePoint(0x2715)}</button>
            </div>
          </div>
        </div>

        <div style={styles.cardBody}>
          {loading ? <div style={styles.loadingText}>Loading...</div> : editing ? (
            <div style={styles.editForm}>
              <div style={styles.sectionTitle}>EDIT JOB</div>

              <div style={styles.editGrid}>
                <div style={styles.field}><label style={styles.label}>JOB TITLE</label><input value={editForm.title || ''} onChange={e => setEdit('title', e.target.value)} style={styles.input} /></div>
                <div style={styles.field}><label style={styles.label}>JOB TYPE</label>
                  <select value={editForm.job_type || ''} onChange={e => setEdit('job_type', e.target.value)} style={styles.input}>
                    {jobTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div style={styles.field}><label style={styles.label}>CLIENT NAME</label><input value={editForm.client_name || ''} onChange={e => setEdit('client_name', e.target.value)} style={styles.input} /></div>
                <div style={styles.field}><label style={styles.label}>CLIENT PHONE</label><input value={editForm.client_phone || ''} onChange={e => setEdit('client_phone', e.target.value)} style={styles.input} /></div>
                <div style={styles.field}><label style={styles.label}>CLIENT EMAIL</label><input value={editForm.client_email || ''} onChange={e => setEdit('client_email', e.target.value)} style={styles.input} /></div>
                <div style={styles.field}><label style={styles.label}>CONTRACT VALUE</label><input value={editForm.contract_value || ''} onChange={e => setEdit('contract_value', e.target.value)} style={styles.input} type="number" /></div>
                <div style={styles.field}><label style={styles.label}>SITE ADDRESS</label><input value={editForm.site_address || ''} onChange={e => setEdit('site_address', e.target.value)} style={styles.input} /></div>
                <div style={styles.field}><label style={styles.label}>CITY</label><input value={editForm.site_city || ''} onChange={e => setEdit('site_city', e.target.value)} style={styles.input} /></div>
                <div style={styles.field}><label style={styles.label}>STATE</label><input value={editForm.site_state || ''} onChange={e => setEdit('site_state', e.target.value)} style={styles.input} placeholder="MI" /></div>
                <div style={styles.field}><label style={styles.label}>START DATE</label><input value={editForm.start_date || ''} onChange={e => setEdit('start_date', e.target.value)} style={styles.input} type="date" /></div>
                <div style={styles.field}><label style={styles.label}>END DATE</label><input value={editForm.end_date || ''} onChange={e => setEdit('end_date', e.target.value)} style={styles.input} type="date" /></div>
                <div style={styles.field}>
                  <label style={styles.label}>CREW LEADER</label>
                  <select value={editForm.crew_leader_id || ''} onChange={e => setEdit('crew_leader_id', e.target.value)} style={styles.input}>
                    <option value="">Unassigned</option>
                    {crewLeaders.map(l => <option key={l.id} value={l.id}>{l.full_name}</option>)}
                  </select>
                </div>
                <div style={styles.field}><label style={styles.label}>EQUIPMENT</label><input value={editForm.equipment_assigned || ''} onChange={e => setEdit('equipment_assigned', e.target.value)} style={styles.input} placeholder="Equipment assigned" /></div>
              </div>

              {fullJob.business_id === 'pools' && (
                <>
                  <div style={styles.sectionTitle}>DAY OF DETAILS</div>
                  <div style={styles.editGrid}>
                    <div style={styles.field}><label style={styles.label}>WATER TRUCK COMPANY</label><input value={editForm.water_truck_company || ''} onChange={e => setEdit('water_truck_company', e.target.value)} style={styles.input} placeholder="Company name" /></div>
                    <div style={styles.field}><label style={styles.label}>WATER TRUCK TIME</label><input value={editForm.water_truck_time || ''} onChange={e => setEdit('water_truck_time', e.target.value)} style={styles.input} placeholder="e.g. 8:00 AM" /></div>
                    <div style={styles.field}><label style={styles.label}>SAND DELIVERY COMPANY</label><input value={editForm.sand_delivery_company || ''} onChange={e => setEdit('sand_delivery_company', e.target.value)} style={styles.input} placeholder="Company name" /></div>
                    <div style={styles.field}><label style={styles.label}>SAND DELIVERY TIME</label><input value={editForm.sand_delivery_time || ''} onChange={e => setEdit('sand_delivery_time', e.target.value)} style={styles.input} placeholder="e.g. 9:00 AM" /></div>
                    <div style={styles.field}><label style={styles.label}>DIRT HAUL AWAY NOTES</label><input value={editForm.dirt_haul_notes || ''} onChange={e => setEdit('dirt_haul_notes', e.target.value)} style={styles.input} placeholder="Notes" /></div>
                  </div>
                  <div style={styles.sectionTitle}>ADD-ONS</div>
                  <div style={styles.checkGrid}>
                    {[
                      { key: 'has_heater', label: 'Heater' },
                      { key: 'has_main_drain', label: 'Main Drain' },
                      { key: 'has_deep_end', label: 'Deep End' },
                      { key: 'dirt_haul_away', label: 'Dirt Haul Away' },
                      { key: 'sand_delivery', label: 'Sand Delivery' },
                      { key: 'has_deck', label: 'Deck' },
                    ].map(({ key, label }) => (
                      <div key={key} onClick={() => setEdit(key, !editForm[key])} style={{ ...styles.checkbox, ...(editForm[key] ? styles.checkboxActive : {}) }}>
                        <div style={{ ...styles.checkDot, ...(editForm[key] ? styles.checkDotActive : {}) }} />
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                  <div style={styles.sectionTitle}>POOL SPECS</div>
                  <div style={styles.editGrid}>
                    <div style={styles.field}><label style={styles.label}>POOL DIMENSIONS</label><input value={editForm.pool_dimensions || ''} onChange={e => setEdit('pool_dimensions', e.target.value)} style={styles.input} /></div>
                    <div style={styles.field}><label style={styles.label}>DIG SPECS</label><input value={editForm.dig_specs || ''} onChange={e => setEdit('dig_specs', e.target.value)} style={styles.input} /></div>
                    <div style={styles.field}>
                      <label style={styles.label}>UTILITY LOCATE</label>
                      <select value={editForm.utility_locate_status || 'pending'} onChange={e => setEdit('utility_locate_status', e.target.value)} style={styles.input}>
                        <option value="pending">Pending</option>
                        <option value="requested">Requested</option>
                        <option value="complete">Complete</option>
                        <option value="clear">Clear - Good to Dig</option>
                      </select>
                    </div>
                  </div>
                  <div style={styles.sectionTitle}>SAND REQUIREMENT</div>
                  <div style={styles.checkGrid}>
                    {[
                      { key: 'sand_required', label: 'Sand Required' },
                      { key: 'is_replacement', label: 'Replacement (half)' },
                      { key: 'installer_provides_sand', label: 'We Haul It' },
                    ].map(function(f) {
                      var active = !!editForm[f.key]
                      return (
                        <div key={f.key} onClick={function() {
                          if (f.key === 'is_replacement') {
                            var std = (sandStandards || []).find(function(st) { return st.pool_size === editForm.pool_dimensions })
                            var nextYards = editForm.sand_yards
                            if (std) nextYards = !active ? Number(std.replacement_yards) : Number(std.new_install_yards)
                            setEditForm(function(f0) { return Object.assign({}, f0, { is_replacement: !active, sand_yards: nextYards }) })
                          } else {
                            setEdit(f.key, !active)
                          }
                        }} style={Object.assign({}, styles.checkbox, active ? styles.checkboxActive : {})}>
                          <div style={Object.assign({}, styles.checkDot, active ? styles.checkDotActive : {})} />
                          <span>{f.label}</span>
                        </div>
                      )
                    })}
                  </div>
                  {editForm.sand_required && (
                    <div style={styles.editGrid}>
                      <div style={styles.field}>
                        <label style={styles.label}>POOL SIZE (STANDARDS)</label>
                        <select value={editForm.pool_dimensions || ''} onChange={function(e) {
                          var poolSize = e.target.value
                          var std = (sandStandards || []).find(function(st) { return st.pool_size === poolSize })
                          var yards = std ? (editForm.is_replacement ? std.replacement_yards : std.new_install_yards) : editForm.sand_yards
                          setEditForm(function(f0) { return Object.assign({}, f0, { pool_dimensions: poolSize, sand_yards: yards != null ? Number(yards) : f0.sand_yards, sand_type: std?.sand_type || f0.sand_type || 'mason sand' }) })
                        }} style={styles.input}>
                          <option value="">Select size...</option>
                          {(sandStandards || []).map(function(st) { return <option key={st.id} value={st.pool_size}>{st.pool_size}</option> })}
                        </select>
                      </div>
                      <div style={styles.field}><label style={styles.label}>SAND YARDS</label><input type="number" step="0.25" min="0" value={editForm.sand_yards != null && editForm.sand_yards !== '' ? editForm.sand_yards : ''} onChange={function(e) { setEdit('sand_yards', e.target.value === '' ? null : parseFloat(e.target.value)) }} style={styles.input} /></div>
                      <div style={styles.field}><label style={styles.label}>SAND TYPE</label><input value={editForm.sand_type || 'mason sand'} onChange={function(e) { setEdit('sand_type', e.target.value) }} style={styles.input} /></div>
                      <div style={{ ...styles.field, gridColumn: '1 / -1' }}><label style={styles.label}>SAND NOTES</label><input value={editForm.sand_notes || ''} onChange={function(e) { setEdit('sand_notes', e.target.value) }} style={styles.input} placeholder="Delivery instructions, etc." /></div>
                    </div>
                  )}
                </>
              )}

              {fullJob.business_id === 'riverbend' && (
                <>
                  <div style={styles.sectionTitle}>SITE SPECS</div>
                  <div style={styles.editGrid}>
                    <div style={styles.field}><label style={styles.label}>ACREAGE</label><input value={editForm.acreage || ''} onChange={e => setEdit('acreage', e.target.value)} style={styles.input} type="number" step="0.1" /></div>
                    <div style={styles.field}><label style={styles.label}>DEBRIS DISPOSAL</label><input value={editForm.debris_disposal || ''} onChange={e => setEdit('debris_disposal', e.target.value)} style={styles.input} /></div>
                    <div style={styles.field}><label style={styles.label}>SITE ACCESS</label><input value={editForm.site_access_notes || ''} onChange={e => setEdit('site_access_notes', e.target.value)} style={styles.input} /></div>
                  </div>
                  <div style={styles.sectionTitle}>WORK SCHEDULE</div>
                  <div
                    onClick={() => setEditMultiVisit(v => !v)}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: editMultiVisit ? 'rgba(74,140,106,0.08)' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (editMultiVisit ? 'rgba(74,140,106,0.3)' : 'rgba(255,255,255,0.08)'), borderRadius: '3px', cursor: 'pointer', marginBottom: '12px' }}
                  >
                    <div style={{ width: '34px', height: '18px', borderRadius: '10px', backgroundColor: editMultiVisit ? '#4a8c6a' : 'rgba(255,255,255,0.15)', position: 'relative' }}>
                      <div style={{ position: 'absolute', top: '2px', left: editMultiVisit ? '18px' : '2px', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#fff' }} />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '1.5px', fontWeight: 600, color: editMultiVisit ? '#4a8c6a' : 'rgba(255,255,255,0.7)' }}>MULTI-VISIT JOB</div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Job spans multiple visits.</div>
                    </div>
                  </div>
                  {editMultiVisit && (
                    <SegmentsEditor segments={editSegments} onChange={setEditSegments} validation={segEditValidation} defaultCrewSize={3} />
                  )}
                </>
              )}

              <div style={styles.field}>
                <div style={{marginBottom:"16px"}}>
  <div style={{fontFamily:"'Barlow Condensed', sans-serif",fontSize:"11px",fontWeight:700,letterSpacing:"2px",color:"rgba(255,255,255,0.3)",marginBottom:"10px",borderBottom:"1px solid rgba(255,255,255,0.06)",paddingBottom:"6px"}}>BACK-BIDDING: ACTUAL COSTS vs BID</div>
  {jobExpenses.length > 0 ? (
    <div>
      {(function() {
        var bidTotal = Number(job.contract_value || job.quoted_price || 0)
        var actualCosts = jobExpenses.reduce(function(s,e) { return s + (parseFloat(e.amount) || 0) }, 0)
        var profit = bidTotal - actualCosts
        var margin = bidTotal > 0 ? Math.round((profit / bidTotal) * 100) : 0
        var fmt = function(n) { return "$" + n.toLocaleString("en-US", {minimumFractionDigits: 2}) }
        return (
          <div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px",marginBottom:"12px"}}>
              <div style={{padding:"12px",backgroundColor:"rgba(74,140,106,0.08)",borderRadius:"4px",border:"1px solid rgba(74,140,106,0.15)"}}>
                <div style={{fontSize:"10px",color:"rgba(255,255,255,0.3)",fontWeight:600,letterSpacing:"1px",fontFamily:"'Barlow Condensed', sans-serif"}}>BID TOTAL</div>
                <div style={{fontSize:"18px",fontWeight:700,color:"#4a8c6a"}}>{fmt(bidTotal)}</div>
              </div>
              <div style={{padding:"12px",backgroundColor:"rgba(224,92,92,0.08)",borderRadius:"4px",border:"1px solid rgba(224,92,92,0.15)"}}>
                <div style={{fontSize:"10px",color:"rgba(255,255,255,0.3)",fontWeight:600,letterSpacing:"1px",fontFamily:"'Barlow Condensed', sans-serif"}}>ACTUAL COSTS</div>
                <div style={{fontSize:"18px",fontWeight:700,color:"#e05c5c"}}>{fmt(actualCosts)}</div>
              </div>
            </div>
            <div style={{padding:"10px",backgroundColor:profit >= 0 ? "rgba(74,140,106,0.08)" : "rgba(224,92,92,0.08)",borderRadius:"4px",marginBottom:"12px",textAlign:"center"}}>
              <div style={{fontSize:"10px",color:"rgba(255,255,255,0.3)",fontWeight:600,letterSpacing:"1px",fontFamily:"'Barlow Condensed', sans-serif"}}>PROFIT / LOSS</div>
              <div style={{fontSize:"20px",fontWeight:700,color:profit >= 0 ? "#4a8c6a" : "#e05c5c"}}>{(profit >= 0 ? "+" : "") + fmt(profit)}</div>
              <div style={{fontSize:"11px",color:"rgba(255,255,255,0.3)",marginTop:"2px"}}>{margin + "% margin"}</div>
            </div>
          </div>
        )
      })()}
      <div style={{fontSize:"10px",color:"rgba(255,255,255,0.3)",fontWeight:600,letterSpacing:"1px",fontFamily:"'Barlow Condensed', sans-serif",marginBottom:"6px"}}>{"EXPENSES (" + jobExpenses.length + ")"}</div>
      {jobExpenses.map(function(exp) { return (
        <div key={exp.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,0.04)",fontSize:"13px"}}>
          <div><span style={{color:"rgba(255,255,255,0.3)",fontSize:"11px",textTransform:"uppercase",marginRight:"8px"}}>{(exp.category || "misc").replace(/_/g, " ")}</span><span style={{color:"rgba(255,255,255,0.7)"}}>{exp.description || exp.vendor || ""}</span></div>
          <div style={{color:"#e05c5c",fontWeight:600}}>{"$" + Number(exp.amount || 0).toLocaleString("en-US", {minimumFractionDigits: 2})}</div>
        </div>
      )})}
    </div>
  ) : (
    <div style={{color:"rgba(255,255,255,0.2)",fontSize:"13px",padding:"8px 0"}}>No expenses logged yet.</div>
  )}
</div>
                <label style={styles.label}>NOTES</label>
                <textarea value={editForm.notes || ''} onChange={e => setEdit('notes', e.target.value)} style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} rows={3} />
              </div>

              <div style={styles.editActions}>
                <button onClick={() => setEditing(false)} style={styles.cancelBtn}>CANCEL</button>
                <button onClick={handleSaveEdit} disabled={saving} style={styles.saveBtn}>{saving ? 'SAVING...' : 'SAVE CHANGES'}</button>
              </div>
            </div>
          ) : (
            <>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>CLIENT</div>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}><div style={styles.infoLabel}>NAME</div><div style={{ ...styles.infoValue, wordBreak: 'break-word', overflowWrap: 'anywhere' }} title={String(fullJob.client_name || '')}>{truncate(fullJob.client_name, 80) || '-'}</div></div>
                  {fullJob.client_phone && <div style={styles.infoItem}><div style={styles.infoLabel}>PHONE</div><a href={'tel:' + String(fullJob.client_phone)} style={{ ...styles.infoValue, color: '#b48c3c', textDecoration: 'none' }}>{truncate(fullJob.client_phone, 40)}</a></div>}
                  {fullJob.client_email && <div style={styles.infoItem}><div style={styles.infoLabel}>EMAIL</div><a href={'mailto:' + String(fullJob.client_email)} style={{ ...styles.infoValue, color: '#b48c3c', textDecoration: 'none', wordBreak: 'break-all' }} title={String(fullJob.client_email)}>{truncate(fullJob.client_email, 80)}</a></div>}
                </div>
              </div>
              {fullJob.site_address && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>SITE ADDRESS</div>
                  <button onClick={openMaps} style={styles.addressBtn}>
                    <div style={styles.addressText} title={String(fullJob.site_address || '')}>{truncate([fullJob.site_address, fullJob.site_city, fullJob.site_state].filter(Boolean).join(', '), 120)}</div>
                    <div style={styles.mapsTag}>OPEN IN MAPS {String.fromCodePoint(0x2192)}</div>
                  </button>
                </div>
              )}

              {/* === SENT QUOTE — frozen snapshot of what the customer received === */}
              {/* Hidden in safeMode (set by the error boundary) so a malformed snapshot can't keep us from rendering the rest of the card. */}
              {!safeMode && canSeePricing && fullJob.quote_snapshot && (() => {
                var snap = fullJob.quote_snapshot || {}
                var snapBid = snap.bid || {}
                var snapAt = fullJob.quote_snapshot_at ?? snap.captured_at ?? null
                var signedAt = snap.signed_at ?? snapBid.signed_at ?? null
                var signerName = snap.signer_name ?? snapBid.signer_name ?? null
                var snapTotal = snapBid.total ?? snapBid.bid_total ?? fullJob.contract_value ?? null
                var bidEditedSince = bid?.updated_at && snapAt && (new Date(bid.updated_at) > new Date(snapAt))
                return (
                  <div style={styles.snapshotCard}>
                    <div style={styles.snapshotHeader}>
                      <div>
                        <div style={styles.snapshotTitle}>SENT QUOTE</div>
                        <div style={styles.snapshotDate}>{snapAt ? new Date(snapAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
                      </div>
                      {snapTotal != null && <div style={styles.snapshotTotal}>${Number(snapTotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>}
                    </div>
                    {signedAt ? (
                      <div style={styles.snapshotSigned}>{'✓ Signed by ' + (signerName || 'customer') + ' on ' + new Date(signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    ) : (
                      <div style={styles.snapshotPending}>Awaiting signature</div>
                    )}
                    <div style={styles.snapshotActions}>
                      <button type="button" onClick={function() { setShowSnapshot(true) }} style={styles.snapshotViewBtn}>VIEW WHAT THE CUSTOMER {signedAt ? 'SIGNED' : 'RECEIVED'} →</button>
                    </div>
                    {bidEditedSince && (
                      <div style={styles.snapshotWarn}>{'⚠ Bid has been edited since this quote was sent. The customer ' + (signedAt ? 'signed' : 'received') + ' the version shown here.'}</div>
                    )}
                  </div>
                )
              })()}

              {/* === BID SYNC — auto-sync controls + last sync indicator === */}
              {canSeePricing && fullJob.bid_id && (() => {
                var lastSyncedAt = fullJob.last_synced_from_bid_at
                var log = Array.isArray(fullJob.sync_log) ? fullJob.sync_log : []
                var lastEntry = log[0]
                var lastErr = lastEntry && lastEntry.outcome === 'error'
                var dotColor = lastErr ? '#e05c5c' : (lastSyncedAt ? '#4a8c6a' : 'rgba(255,255,255,0.25)')
                var autoOn = fullJob.auto_sync_from_bid !== false
                var canSync = role === 'owner' || role === 'office_staff' || role === 'project_manager'
                return (
                  <div style={styles.syncBox}>
                    <div style={styles.syncHeaderRow}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dotColor }} />
                        <div style={styles.syncLabel}>BID SYNC</div>
                      </div>
                      {role === 'owner' && (
                        <button type="button" onClick={toggleAutoSync} disabled={autoSyncSaving} style={styles.syncToggleBtn}>
                          <div style={{ width: '28px', height: '16px', borderRadius: '10px', backgroundColor: autoOn ? '#4a8c6a' : 'rgba(255,255,255,0.15)', position: 'relative' }}>
                            <div style={{ position: 'absolute', top: '2px', left: autoOn ? '14px' : '2px', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#fff' }} />
                          </div>
                          <span style={{ marginLeft: '8px', fontSize: '11px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1.5px', color: autoOn ? '#4a8c6a' : 'rgba(255,255,255,0.4)' }}>{autoOn ? 'AUTO-SYNC ON' : 'AUTO-SYNC OFF'}</span>
                        </button>
                      )}
                    </div>
                    <div style={styles.syncMeta}>
                      {lastSyncedAt ? ('Last synced from bid: ' + timeAgo(lastSyncedAt)) : 'Not yet synced from bid.'}
                      {lastErr && <span style={{ color: '#e05c5c', marginLeft: '8px' }}>Sync error — see log</span>}
                    </div>
                    <div style={styles.syncActions}>
                      {canSync && (
                        <button type="button" onClick={runManualSync} disabled={syncing} style={styles.syncNowBtn}>{syncing ? 'SYNCING…' : 'SYNC FROM BID NOW'}</button>
                      )}
                      {log.length > 0 && (
                        <button type="button" onClick={function() { setShowSyncLog(true) }} style={styles.syncLogBtn}>VIEW LOG ({log.length})</button>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* === WORK SCOPE — hero section, shows linked bid lines + store extras === */}
              <div style={styles.scopeSection}>
                <div style={styles.scopeHeader}>
                  <div style={styles.scopeTitle}>WORK SCOPE{bid && bid.job_name ? ' \u2014 ' + bid.job_name : ''}</div>
                  {canSeePricing && bid && bid.total != null && <div style={styles.scopeTotal}>${Number(bid.total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>}
                </div>
                {!fullJob.bid_id ? (
                  <div style={styles.scopeEmpty}>
                    <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>{String.fromCodePoint(0x1F4C4)} NO BID LINKED</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>This job was created without a linked bid.</div>
                    {canEdit && (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button type="button" onClick={function() { setShowLinkBid(true); fetchLinkableBids() }} style={styles.scopeEditBtn}>LINK EXISTING BID</button>
                        <button type="button" disabled={creatingBid} onClick={createBidFromJob} style={styles.scopeEditBtn}>{creatingBid ? 'CREATING…' : 'CREATE BID FROM JOB'}</button>
                      </div>
                    )}
                  </div>
                ) : bidLoading ? (
                  <div style={styles.scopeEmpty}>Loading line items\u2026</div>
                ) : bidLines.length === 0 ? (
                  <div style={styles.scopeEmpty}>Bid has no line items.</div>
                ) : (
                  (function() {
                    // Group by category if populated, otherwise a single group.
                    var hasCategories = bidLines.some(function(l) { return l.category })
                    var groups = {}
                    if (hasCategories) {
                      bidLines.forEach(function(l) {
                        var key = l.category || 'Other'
                        if (!groups[key]) groups[key] = []
                        groups[key].push(l)
                      })
                    } else {
                      groups['Line Items'] = bidLines.slice()
                    }
                    var groupNames = Object.keys(groups)
                    return groupNames.map(function(name) {
                      var rows = groups[name]
                      var groupTotal = rows.reduce(function(s, l) { return s + (parseFloat(l.total_cost) || 0) }, 0)
                      return (
                        <div key={name} style={styles.scopeGroup}>
                          {hasCategories && <div style={styles.scopeGroupTitle}>{name.toUpperCase()}</div>}
                          <div style={styles.scopeTable}>
                            {rows.map(function(line) {
                              return (
                                <div key={line.id} style={styles.scopeRow}>
                                  <div style={styles.scopeRowDesc}>{line.description}</div>
                                  <div style={styles.scopeRowQty}>{line.quantity != null ? line.quantity + (line.unit ? ' ' + line.unit : '') : ''}</div>
                                  {canSeePricing && <div style={styles.scopeRowPrice}>{line.total_cost != null ? '$' + Number(line.total_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</div>}
                                </div>
                              )
                            })}
                          </div>
                          {canSeePricing && hasCategories && groupNames.length > 1 && (
                            <div style={styles.scopeGroupTotal}>Subtotal: ${Number(groupTotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          )}
                        </div>
                      )
                    })
                  })()
                )}
                {storeExtras && (
                  <div style={styles.scopeGroup}>
                    <div style={styles.scopeGroupTitle}>EXTRAS</div>
                    <div style={styles.scopeTable}>
                      {storeExtras.tear_down && (
                        <div style={styles.scopeRow}>
                          <div style={styles.scopeRowDesc}>{String.fromCodePoint(0x2611, 0xFE0F)} Tear Down & Place by Road</div>
                          <div style={styles.scopeRowQty} />
                          {canSeePricing && <div style={styles.scopeRowPrice}>$500.00</div>}
                        </div>
                      )}
                      {storeExtras.half_sunk && (
                        <div style={styles.scopeRow}>
                          <div style={styles.scopeRowDesc}>{String.fromCodePoint(0x2611, 0xFE0F)} Half Sunk Aboveground</div>
                          <div style={styles.scopeRowQty} />
                          {canSeePricing && <div style={styles.scopeRowPrice}>TBD</div>}
                        </div>
                      )}
                      {Array.isArray(storeExtras.extras) && storeExtras.extras.map(function(ex, i) {
                        return (
                          <div key={'ex' + i} style={styles.scopeRow}>
                            <div style={styles.scopeRowDesc}>{String.fromCodePoint(0x2611, 0xFE0F)} {ex.description || ex.name || 'Extra'}</div>
                            <div style={styles.scopeRowQty} />
                            {canSeePricing && <div style={styles.scopeRowPrice}>{ex.price != null && ex.price !== 'TBD' ? '$' + Number(ex.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'TBD'}</div>}
                          </div>
                        )
                      })}
                      {Array.isArray(storeExtras.custom_items) && storeExtras.custom_items.map(function(ci, i) {
                        return (
                          <div key={'ci' + i} style={styles.scopeRow}>
                            <div style={styles.scopeRowDesc}>{String.fromCodePoint(0x2611, 0xFE0F)} {ci.description || 'Custom'}</div>
                            <div style={styles.scopeRowQty} />
                            {canSeePricing && <div style={styles.scopeRowPrice}>{ci.price === 'TBD' || ci.price === 'tbd' ? 'TBD' : (ci.price != null ? '$' + Number(ci.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'TBD')}</div>}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                {(bid?.notes || storeExtras?.notes) && (
                  <div style={styles.scopeNotes}>
                    <div style={styles.scopeGroupTitle}>NOTES</div>
                    {bid?.notes && <div style={styles.scopeNotesBody}>{bid.notes}</div>}
                    {storeExtras?.notes && <div style={styles.scopeNotesBody}>{storeExtras.notes}</div>}
                  </div>
                )}
                {bid?.disclaimers && (
                  <div style={styles.scopeNotes}>
                    <div style={styles.scopeGroupTitle}>DISCLAIMERS</div>
                    <div style={styles.scopeNotesBody}>{bid.disclaimers}</div>
                  </div>
                )}
              </div>

              <div style={styles.section}>
                <div style={styles.sectionTitle}>SCHEDULE</div>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}><div style={styles.infoLabel}>START</div><div style={styles.infoValue}>{fullJob.start_date || '-'}</div></div>
                  <div style={styles.infoItem}><div style={styles.infoLabel}>END</div><div style={styles.infoValue}>{fullJob.end_date || '-'}</div></div>
                </div>
              </div>
              {fullJob.profiles && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>CREW LEADER</div>
                  <div style={styles.infoGrid}>
                    <div style={styles.infoItem}><div style={styles.infoLabel}>NAME</div><div style={styles.infoValue}>{fullJob.profiles.full_name}</div></div>
                    {fullJob.profiles.phone && <div style={styles.infoItem}><div style={styles.infoLabel}>PHONE</div><a href={'tel:' + fullJob.profiles.phone} style={{ ...styles.infoValue, color: '#b48c3c', textDecoration: 'none' }}>{fullJob.profiles.phone}</a></div>}
                  </div>
                </div>
              )}
              {jobCrew.length > 0 && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>SCHEDULED CREW</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {Array.from(new Set(jobCrew.map(function(r) { return r.schedule_date }))).sort().map(function(date) {
                      var rows = jobCrew.filter(function(r) { return r.schedule_date === date && r.status === 'scheduled' })
                      var dt = new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                      return (
                        <div key={date} style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)' }}>
                          <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1px', color: '#b48c3c', marginRight: '8px' }}>{dt.toUpperCase()}</span>
                          {rows.map(function(r) { return r.profiles?.full_name }).filter(Boolean).join(', ') || '(no crew)'}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {fullJob.business_id === 'pools' && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>POOL DETAILS</div>
                  <div style={styles.infoGrid}>
                    {fullJob.job_type && <div style={styles.infoItem}><div style={styles.infoLabel}>TYPE</div><div style={styles.infoValue}>{fullJob.job_type.replace('_', ' ').toUpperCase()}</div></div>}
                    {fullJob.pool_dimensions && <div style={styles.infoItem}><div style={styles.infoLabel}>DIMENSIONS</div><div style={styles.infoValue}>{fullJob.pool_dimensions}</div></div>}
                    {fullJob.dig_specs && <div style={styles.infoItem}><div style={styles.infoLabel}>DIG SPECS</div><div style={styles.infoValue}>{fullJob.dig_specs}</div></div>}
                    <div style={styles.infoItem}><div style={styles.infoLabel}>UTILITY LOCATE</div><div style={{ ...styles.infoValue, color: fullJob.utility_locate_status === 'complete' || fullJob.utility_locate_status === 'clear' ? '#4a8c6a' : '#e05c5c' }}>{(fullJob.utility_locate_status || 'PENDING').toUpperCase()}</div></div>
                  </div>
                  {(() => {
                    var icon = !fullJob.sand_required ? '—' : fullJob.installer_provides_sand ? String.fromCodePoint(0x1F69A) : String.fromCodePoint(0x1F3D6, 0xFE0F)
                    var color = !fullJob.sand_required ? 'rgba(255,255,255,0.4)' : fullJob.installer_provides_sand ? '#4a6a8c' : '#b48c3c'
                    return (
                      <div style={{ marginTop: '10px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid ' + color + '40', borderLeft: '3px solid ' + color, borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ fontSize: '18px' }}>{icon}</div>
                        <div>
                          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: color }}>SAND</div>
                          <div style={{ fontSize: '14px', color: '#ffffff' }}>{summarizeSand(fullJob)}</div>
                          {fullJob.sand_notes && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>{fullJob.sand_notes}</div>}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
              {fullJob.business_id === 'pools' && (
                <ScopePanel job={fullJob} onJobUpdated={function(patch) { setFullJob(function(j) { return Object.assign({}, j, patch) }) }} canEdit={canEdit} />
              )}
              {fullJob.business_id === 'riverbend' && fullJob.has_segments && segments.length > 0 && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>WORK SCHEDULE — {segments.length} VISIT{segments.length === 1 ? '' : 'S'}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {segments.map(function(seg) {
                      var status = seg.status || 'scheduled'
                      var color = status === 'completed' ? '#4a8c6a' : status === 'in_progress' ? '#b48c3c' : 'rgba(255,255,255,0.35)'
                      var icon = status === 'completed' ? String.fromCodePoint(0x2713) : status === 'in_progress' ? String.fromCodePoint(0x2192) : ' '
                      var dateLabel = seg.start_date + (seg.end_date && seg.end_date !== seg.start_date ? ' → ' + seg.end_date : '')
                      return (
                        <div key={seg.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid ' + color, borderRadius: '3px' }}>
                          <div style={{ width: '22px', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', color: color, fontWeight: 700, textAlign: 'center' }}>{icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', color: '#ffffff' }}>
                              <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1.5px', color: '#4a8c6a', marginRight: '8px' }}>VISIT {seg.segment_number}</span>
                              {dateLabel}
                            </div>
                            {seg.description && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginTop: '2px' }}>{seg.description}</div>}
                            <div style={{ fontSize: '11px', color: color, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1px', marginTop: '2px' }}>{status.replace('_', ' ').toUpperCase()}</div>
                          </div>
                          {canEdit && status !== 'completed' && (
                            <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                              {status !== 'in_progress' && <button onClick={function() { markSegmentStatus(seg.id, 'in_progress') }} style={{ padding: '4px 8px', backgroundColor: 'rgba(180,140,60,0.12)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '2px', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1px', cursor: 'pointer' }}>START</button>}
                              <button onClick={function() { markSegmentStatus(seg.id, 'completed') }} style={{ padding: '4px 8px', backgroundColor: 'rgba(74,140,106,0.12)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '2px', color: '#4a8c6a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1px', cursor: 'pointer' }}>DONE</button>
                            </div>
                          )}
                          {canEdit && status === 'completed' && (
                            <button onClick={function() { markSegmentStatus(seg.id, 'scheduled') }} style={{ padding: '4px 8px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1px', cursor: 'pointer' }}>REOPEN</button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {fullJob.business_id === 'riverbend' && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>SITE DETAILS</div>
                  <div style={styles.infoGrid}>
                    {fullJob.job_type && <div style={styles.infoItem}><div style={styles.infoLabel}>TYPE</div><div style={styles.infoValue}>{fullJob.job_type.replace('_', ' ').toUpperCase()}</div></div>}
                    {fullJob.acreage && <div style={styles.infoItem}><div style={styles.infoLabel}>ACREAGE</div><div style={styles.infoValue}>{fullJob.acreage} acres</div></div>}
                    {fullJob.debris_disposal && <div style={styles.infoItem}><div style={styles.infoLabel}>DEBRIS DISPOSAL</div><div style={styles.infoValue}>{fullJob.debris_disposal}</div></div>}
                    {fullJob.site_access_notes && <div style={styles.infoItem}><div style={styles.infoLabel}>SITE ACCESS</div><div style={styles.infoValue}>{fullJob.site_access_notes}</div></div>}
                  </div>
                </div>
              )}
              {/* === COMPLETION + PAYMENT PANEL === */}
              {(function() {
                var isComplete = fullJob.status === 'completed'
                var hasAmount = fullJob.amount_collected != null
                var bidTotal = bid && bid.total != null ? Number(bid.total) : (fullJob.contract_value != null ? Number(fullJob.contract_value) : null)

                // CANCELLED: its own terminal state. No completion panel, no
                // "due on site", and no way to mark it complete from here.
                if (isCancelled(fullJob)) {
                  return (
                    <div style={styles.cancelledPanel}>
                      <div style={styles.cancelledTitle}>{String.fromCodePoint(0x2298)} JOB CANCELLED</div>
                      <div style={styles.cancelledNote}>
                        This job was called off and is not billable. Nothing is due on site.
                        {bidTotal != null && canSeeCollectedAmount ? ' Original bid was $' + bidTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '.' : ''}
                      </div>
                      {canEdit && (
                        <div style={styles.cancelledHint}>Set the status back to Scheduled above to reinstate it.</div>
                      )}
                    </div>
                  )
                }

                // LABORERS: just the green check, nothing else.
                if (role === 'laborer') {
                  if (!isComplete) return null
                  return (
                    <div style={styles.completedPanelLite}>
                      <div style={styles.completedTitle}>{String.fromCodePoint(0x2713)} JOB COMPLETED</div>
                      {fullJob.completed_at && <div style={styles.completedDate}>{new Date(fullJob.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
                    </div>
                  )
                }

                // CREW LEADERS: green check + "Paid" status, no dollar amounts.
                if (!canSeeCollectedAmount && isComplete) {
                  return (
                    <div style={styles.completedPanel}>
                      <div style={styles.completedTitle}>{String.fromCodePoint(0x2713)} JOB COMPLETED{hasAmount ? ' \u2014 PAID' : ''}</div>
                      {fullJob.completed_at && <div style={styles.completedDate}>{new Date(fullJob.completed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>}
                      {fullJob.payment_method && <div style={styles.paymentMethodLine}>Paid via {fullJob.payment_method}</div>}
                    </div>
                  )
                }

                // COMPLETE + AMOUNT RECORDED — full panel for owner/office/PM.
                if (isComplete && hasAmount) {
                  var collected = Number(fullJob.amount_collected)
                  var variance = bidTotal != null ? collected - bidTotal : null
                  return (
                    <div style={styles.completedPanel}>
                      <div style={styles.completedTitle}>{String.fromCodePoint(0x2713)} JOB COMPLETED{fullJob.completed_at ? ' \u2014 ' + new Date(fullJob.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
                      <div style={styles.collectedLabel}>TOTAL COLLECTED</div>
                      <div style={styles.collectedAmount}>${collected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      {variance != null && variance !== 0 && (
                        <div style={Object.assign({}, styles.varianceLine, { color: variance > 0 ? '#4a8c6a' : '#e05c5c' })}>
                          Bid: ${bidTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} \u2192 Collected: ${collected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({variance > 0 ? '+' : ''}${variance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </div>
                      )}
                      <div style={styles.paymentMetaRow}>
                        {fullJob.payment_method && <div><span style={styles.paymentMetaLabel}>METHOD</span><span style={styles.paymentMetaValue}>{fullJob.payment_method}</span></div>}
                        {completedByName && <div><span style={styles.paymentMetaLabel}>BY</span><span style={styles.paymentMetaValue}>{completedByName}</span></div>}
                      </div>
                      {fullJob.payment_notes && (
                        <div style={styles.paymentNotesBlock}>
                          <div style={styles.paymentMetaLabel}>NOTES</div>
                          <div style={styles.paymentNotesText}>{fullJob.payment_notes}</div>
                        </div>
                      )}
                      {canEditCollected && <button type="button" onClick={openCompleteModal} style={styles.editCollectedBtn}>EDIT COLLECTED AMOUNT</button>}
                      {showFinancials && <button onClick={() => setShowInvoice(true)} style={{ ...styles.collectBtn, backgroundColor: 'rgba(74,106,140,0.15)', borderColor: 'rgba(74,106,140,0.3)', color: '#4a6a8c', marginTop: '10px' }}>SEND PAID INVOICE</button>}
                    </div>
                  )
                }

                // COMPLETE BUT NO AMOUNT — backfill prompt (one of the 9 legacy rows).
                if (isComplete && !hasAmount) {
                  return (
                    <div style={styles.completedPanel}>
                      <div style={styles.completedTitle}>{String.fromCodePoint(0x2713)} JOB COMPLETED{fullJob.completed_at ? ' \u2014 ' + new Date(fullJob.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
                      <div style={styles.collectedLabel}>NO AMOUNT RECORDED</div>
                      {canMarkComplete && <button type="button" onClick={openCompleteModal} style={styles.completeBtn}>SET COLLECTED AMOUNT</button>}
                    </div>
                  )
                }

                // NOT YET COMPLETE — show the Mark Complete button + existing payment buttons for above-ground / mulching.
                return (
                  <div style={{ ...styles.section, backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '4px', padding: '16px' }}>
                    {showFinancials && canViewDueOnSite && (() => {
                      const due = resolveDueOnSite(fullJob)
                      const isOverridden = fullJob.due_on_site != null
                      return (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={styles.sectionTitle}>DUE ON SITE</div>
                            {canEditDueOnSite && (
                              <button type="button" onClick={openDueEdit} style={{ padding: '4px 10px', backgroundColor: 'transparent', border: '1px solid rgba(180,140,60,0.4)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' }}>EDIT</button>
                            )}
                          </div>
                          <div style={styles.contractValue}>${Number(due).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          {fullJob.due_on_site_notes && (
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginTop: '-4px', marginBottom: '8px', fontStyle: 'italic' }}>{fullJob.due_on_site_notes}</div>
                          )}
                          {isOverridden && Math.abs(Number(fullJob.contract_value || 0) - due) > 0.01 && (
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '6px' }}>
                              Bid total: ${Number(fullJob.contract_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (override active)
                            </div>
                          )}
                          <div style={styles.paymentStatus}>Status: <span style={{ color: '#b48c3c' }}>PENDING COLLECTION</span></div>
                        </>
                      )
                    })()}
                    {!showFinancials && bidTotal != null && (
                      <div style={{ marginBottom: '10px' }}>
                        <div style={styles.collectedLabel}>BID TOTAL</div>
                        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '24px', fontWeight: 700, color: '#b48c3c' }}>${bidTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    )}
                    {canMarkComplete && <button type="button" onClick={openCompleteModal} style={styles.completeBtn}>{String.fromCodePoint(0x2713)} MARK JOB COMPLETE</button>}
                    {showFinancials && <button onClick={() => setShowPayment(true)} style={{ ...styles.collectBtn, marginTop: '8px' }}>COLLECT PAYMENT + SEND RECEIPT</button>}
                    {showFinancials && <button onClick={() => setShowInvoice(true)} style={{ ...styles.collectBtn, backgroundColor: 'rgba(74,106,140,0.15)', borderColor: 'rgba(74,106,140,0.3)', color: '#4a6a8c', marginTop: '8px' }}>SEND PAID INVOICE</button>}
                  </div>
                )
              })()}
              {fullJob.notes && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>NOTES</div>
                  <div style={styles.notes}>{fullJob.notes}</div>
                </div>
              )}
              {/* Clock In/Out for pool jobs */}
              {canClock && fullJob.status !== 'completed' && fullJob.status !== 'cancelled' && (
                <div style={{ ...styles.section, backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.15)', borderRadius: '4px', padding: '16px' }}>
                  <div style={styles.sectionTitle}>JOB TIMER (CREW LEADER)</div>
                  {fullJob.estimated_hours && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>Expected: {fullJob.estimated_hours} hrs</div>}
                  {timeEntries.filter(function(e) { return !e.clock_out }).length > 0 && (
                    <div style={{ fontSize: '11px', color: '#4a8c6a', marginBottom: '8px' }}>Currently clocked in: {timeEntries.filter(function(e) { return !e.clock_out }).map(function(e) { return e.profiles?.full_name || 'Crew' }).join(', ')}</div>
                  )}
                  {myClock ? (
                    <button onClick={handleClockOut} style={{ width: '100%', padding: '12px', backgroundColor: '#e05c5c', border: 'none', borderRadius: '3px', color: '#ffffff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' }}>STOP JOB TIMER</button>
                  ) : (
                    <button onClick={handleClockIn} style={{ width: '100%', padding: '12px', backgroundColor: '#4a8c6a', border: 'none', borderRadius: '3px', color: '#ffffff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' }}>START JOB TIMER</button>
                  )}
                  {timeEntries.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      {timeEntries.slice(0, 10).map(function(entry) {
                        var isOwnerOrPM = role === 'owner' || role === 'project_manager'
                        var isMine = entry.user_id === profile?.id
                        var canEditEntry = isMine || isOwnerOrPM
                        return <div key={entry.id} style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', padding: '4px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{entry.profiles?.full_name || 'Crew'}: {entry.hours_worked ? entry.hours_worked.toFixed(1) + ' hrs' : 'In progress'}{entry.edited_at ? ' (Edited)' : ''}</span>
                          {canEditEntry && entry.clock_out && <button onClick={function() {
                            var newIn = prompt('Clock In (HH:MM AM/PM):', new Date(entry.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }))
                            if (!newIn) return
                            var newOut = prompt('Clock Out (HH:MM AM/PM):', new Date(entry.clock_out).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }))
                            if (!newOut) return
                            var reason = prompt('Reason for edit:')
                            if (!reason) { alert('Edit reason is required'); return }
                            var dateBase = entry.clock_in.split('T')[0]
                            var parseTime = function(t, base) { var d = new Date(base + 'T12:00:00'); var m = t.match(/(\d+):(\d+)\s*(AM|PM)/i); if (!m) return null; var h = parseInt(m[1]); var min = parseInt(m[2]); if (m[3].toUpperCase() === 'PM' && h < 12) h += 12; if (m[3].toUpperCase() === 'AM' && h === 12) h = 0; d.setHours(h, min, 0, 0); return d.toISOString() }
                            var newClockIn = parseTime(newIn, dateBase)
                            var newClockOut = parseTime(newOut, dateBase)
                            if (!newClockIn || !newClockOut) { alert('Invalid time format'); return }
                            var hrs = (new Date(newClockOut) - new Date(newClockIn)) / 3600000
                            var updates = { clock_in: newClockIn, clock_out: newClockOut, hours_worked: Math.round(hrs * 100) / 100, edited_at: new Date().toISOString(), edited_by: profile.id, edit_reason: reason }
                            if (!entry.original_clock_in) { updates.original_clock_in = entry.clock_in; updates.original_clock_out = entry.clock_out }
                            supabase.from('job_time_entries').update(updates).eq('id', entry.id).then(function() {
                              setTimeEntries(function(prev) { return prev.map(function(te) { return te.id === entry.id ? Object.assign({}, te, updates) : te }) })
                            })
                          }} style={{ padding: '2px 6px', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '2px', color: '#b48c3c', fontSize: '9px', cursor: 'pointer', fontFamily: "'Barlow Condensed',sans-serif" }}>EDIT</button>}
                        </div>
                      })}
                    </div>
                  )}
                </div>
              )}
              {canETA && fullJob.site_address && fullJob.status !== 'completed' && fullJob.status !== 'cancelled' && (
                <div style={{ marginBottom: '8px' }}>
                  <button onClick={openETA} style={{ padding: '10px 16px', backgroundColor: 'rgba(180,140,60,0.12)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer', width: '100%' }}>SEND ETA</button>
                </div>
              )}
              {fullJob.client_email && (
                <div style={{ marginBottom: '12px' }}>
                  <button onClick={openConfirmEmail} style={{ padding: '10px 16px', backgroundColor: 'rgba(74,140,106,0.12)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', color: '#4a8c6a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer', width: '100%' }}>RESEND CONFIRMATION EMAIL</button>
                </div>
              )}
              {/* Expected Hours edit for owner */}
              {(role === 'owner' || role === 'office_staff') && fullJob.business_id === 'pools' && editing && (
                <div style={styles.field}><label style={styles.label}>EXPECTED HOURS</label><input type="number" value={editForm.estimated_hours || ''} onChange={function(e) { setEdit('estimated_hours', e.target.value) }} style={styles.input} step="0.5" placeholder="e.g. 5" /></div>
              )}
              {canEdit && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>UPDATE STATUS</div>
                  <div style={styles.statusBtns}>
                    {['scheduled', 'active', 'on_hold', 'completed', 'cancelled'].map(s => (
                      <button key={s} onClick={() => handleStatusChange(s)} style={{ ...styles.statusBtn, ...(fullJob.status === s ? { backgroundColor: STATUS_COLORS[s], color: '#0a0f1a' } : {}) }}>
                        {s.replace('_', ' ').toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {showInvoice && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '24px' }} onClick={() => setShowInvoice(false)}>
            <div style={{ backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '32px', width: '100%', maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '2px', color: '#fff' }}>SEND PAID INVOICE</div>
                <button onClick={() => setShowInvoice(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' }}>X</button>
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginBottom: '16px' }}>A PDF invoice marked PAID will be generated and sent.</div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' }}>CLIENT EMAIL</label>
                <input defaultValue={fullJob.client_email || ''} id="inv-email" style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }} placeholder="client@email.com" />
                <button onClick={() => sendJobInvoice('email', document.getElementById('inv-email').value)} disabled={invoiceSending} style={{ width: '100%', padding: '12px', border: 'none', borderRadius: '4px', color: '#fff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer', backgroundColor: invoiceSending ? '#333' : '#4a6a8c', marginTop: '8px' }}>{invoiceSending ? 'SENDING...' : 'EMAIL INVOICE'}</button>
              </div>
              <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.08)', margin: '12px 0' }} />
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' }}>CLIENT PHONE</label>
                <input defaultValue={fullJob.client_phone || ''} id="inv-phone" style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }} placeholder="(810) 555-1234" />
                <button onClick={() => sendJobInvoice('sms', document.getElementById('inv-phone').value)} disabled={invoiceSending} style={{ width: '100%', padding: '12px', border: 'none', borderRadius: '4px', color: '#fff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer', backgroundColor: invoiceSending ? '#333' : '#4a8c6a', marginTop: '8px' }}>{invoiceSending ? 'SENDING...' : 'TEXT INVOICE'}</button>
              </div>
            </div>
          </div>
        )}

        {showPayment && <PaymentModal job={fullJob} onClose={() => setShowPayment(false)} onSuccess={() => { setShowPayment(false); handleStatusChange('completed') }} />}

        {showDueEdit && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1003, padding: '20px' }} onClick={function() { if (!dueSaving) setShowDueEdit(false) }}>
            <div style={{ backgroundColor: '#0f1420', border: '1px solid rgba(180,140,60,0.35)', borderRadius: '6px', padding: '24px', width: '100%', maxWidth: '440px' }} onClick={function(e) { e.stopPropagation() }}>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '16px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c', marginBottom: '14px' }}>EDIT DUE ON SITE</div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '14px' }}>Bid Total: <span style={{ color: '#ffffff', fontWeight: 600 }}>{fmtMoneyDOS(bidTotalForJob(fullJob))}</span> <span style={{ color: 'rgba(255,255,255,0.3)' }}>(reference)</span></div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>AMOUNT DUE ON SITE</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={dueForm.amount}
                  disabled={dueForm.useBidTotal}
                  onChange={function(e) { setDueForm(Object.assign({}, dueForm, { amount: e.target.value })) }}
                  style={{ width: '100%', padding: '10px 12px', backgroundColor: dueForm.useBidTotal ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: dueForm.useBidTotal ? 'rgba(255,255,255,0.3)' : '#ffffff', fontSize: '14px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' }}
                  placeholder="0.00"
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', cursor: 'pointer', marginBottom: '12px' }}>
                <input
                  type="checkbox"
                  checked={dueForm.useBidTotal}
                  onChange={function(e) { setDueForm(Object.assign({}, dueForm, { useBidTotal: e.target.checked, amount: e.target.checked ? String(bidTotalForJob(fullJob) || '') : dueForm.amount })) }}
                  style={{ width: '16px', height: '16px', accentColor: '#b48c3c' }}
                />
                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>Use bid total ({fmtMoneyDOS(bidTotalForJob(fullJob))})</span>
              </label>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>NOTES (OPTIONAL)</label>
                <input
                  value={dueForm.notes}
                  onChange={function(e) { setDueForm(Object.assign({}, dueForm, { notes: e.target.value })) }}
                  placeholder="$1000 deposit already paid, balance $615"
                  style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" disabled={dueSaving} onClick={function() { setShowDueEdit(false) }} style={{ padding: '10px 16px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' }}>CANCEL</button>
                <button type="button" disabled={dueSaving} onClick={saveDueEdit} style={{ flex: 1, padding: '10px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' }}>{dueSaving ? 'SAVING...' : 'SAVE'}</button>
              </div>
            </div>
          </div>
        )}

        {showComplete && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={function() { if (!completing) setShowComplete(false) }}>
            <div style={{ backgroundColor: '#0d1420', border: '1px solid rgba(74,140,106,0.4)', borderRadius: '6px', padding: '28px', width: '100%', maxWidth: '440px' }} onClick={function(e) { e.stopPropagation() }}>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '2px', color: '#4a8c6a', marginBottom: '6px' }}>{fullJob.status === 'completed' ? 'EDIT COLLECTED AMOUNT' : 'COMPLETE THIS JOB?'}</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>{fullJob.job_name || fullJob.client_name || 'Job'}</div>
              {bid && bid.total != null && (
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '16px' }}>Bid Total: <span style={{ color: '#b48c3c', fontWeight: 600 }}>${Number(bid.total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
              )}
              <div style={{ marginBottom: '12px' }}>
                <label style={styles.label}>AMOUNT COLLECTED</label>
                <input
                  type="number"
                  step="0.01"
                  value={completeForm.amount_collected || ''}
                  onChange={function(e) { setCompleteForm(Object.assign({}, completeForm, { amount_collected: e.target.value })) }}
                  autoFocus
                  style={styles.input}
                  placeholder="0.00"
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={styles.label}>PAYMENT METHOD</label>
                <select
                  value={completeForm.payment_method || 'Cash'}
                  onChange={function(e) { setCompleteForm(Object.assign({}, completeForm, { payment_method: e.target.value })) }}
                  style={styles.input}
                >
                  {['Cash', 'Check', 'Card', 'Financing', 'Multiple', 'Other'].map(function(m) { return <option key={m} value={m}>{m}</option> })}
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={styles.label}>NOTES (OPTIONAL)</label>
                <textarea
                  value={completeForm.payment_notes || ''}
                  onChange={function(e) { setCompleteForm(Object.assign({}, completeForm, { payment_notes: e.target.value })) }}
                  placeholder="e.g. $1500 cash + $115 check #1234"
                  style={Object.assign({}, styles.input, { minHeight: '60px', resize: 'vertical' })}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={function() { setShowComplete(false) }} disabled={completing} style={styles.cancelBtn}>CANCEL</button>
                <button type="button" onClick={handleMarkComplete} disabled={completing} style={{ ...styles.saveBtn, backgroundColor: '#4a8c6a', color: '#ffffff' }}>{completing ? 'SAVING\u2026' : (fullJob.status === 'completed' ? 'SAVE' : 'CONFIRM COMPLETE')}</button>
              </div>
            </div>
          </div>
        )}

        {showETA && <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1002,padding:'24px'}} onClick={function(){setShowETA(false)}}>
          <div style={{backgroundColor:'#0f1420',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',padding:'28px',width:'100%',maxWidth:'500px',maxHeight:'85vh',overflow:'auto'}} onClick={function(e){e.stopPropagation()}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,letterSpacing:'2px',color:'#b48c3c'}}>SEND ETA</div>
              <button onClick={function(){setShowETA(false)}} style={{background:'none',border:'none',color:'rgba(255,255,255,0.4)',fontSize:'18px',cursor:'pointer'}}>X</button>
            </div>
            {etaData.loading ? <div style={{padding:'30px',textAlign:'center',color:'rgba(255,255,255,0.3)'}}>Calculating drive time...</div> : (
              <div>
                <div style={{fontSize:'12px',color:'rgba(255,255,255,0.3)',marginBottom:'6px'}}>From: {etaData.fromLabel || 'Shop'}</div>
                <div style={{fontSize:'12px',color:'rgba(255,255,255,0.3)',marginBottom:'12px'}}>To: {fullJob.site_address}</div>
                {etaData.drive !== null && <div style={{padding:'12px',backgroundColor:'rgba(180,140,60,0.08)',border:'1px solid rgba(180,140,60,0.2)',borderRadius:'4px',marginBottom:'12px'}}>
                  <div style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',marginBottom:'4px'}}>Drive time: {etaData.driveText || (etaData.drive + ' min')}{etaData.lunch ? ' + 30 min lunch = ' + (etaData.drive + 30) + ' min' : ''}</div>
                  <div style={{fontSize:'20px',fontWeight:700,fontFamily:"'Barlow Condensed',sans-serif",color:'#b48c3c',letterSpacing:'1px'}}>Estimated arrival: {etaData.etaStr}</div>
                </div>}
                <label style={{display:'flex',gap:'8px',alignItems:'center',cursor:'pointer',marginBottom:'16px'}}>
                  <input type="checkbox" checked={etaData.lunch} onChange={function(e){
                    var lunchMin = e.target.checked ? 30 : 0
                    var totalMin = (etaData.drive || 0) + lunchMin
                    var newEta = new Date(Date.now() + totalMin * 60000)
                    var newStr = newEta.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                    var msg = 'Hi ' + etaData.firstName + ', this is ' + etaData.bizName + '. We\'re ' + (e.target.checked ? 'heading your way with a quick lunch stop' : 'on our way to your property') + ' and expect to arrive around ' + newStr + '. We\'ll give you a call when we\'re close. Thanks!'
                    setEtaData(function(d) { return Object.assign({}, d, { lunch: e.target.checked, etaStr: newStr, message: msg }) })
                  }} />
                  <span style={{fontSize:'13px',color:'rgba(255,255,255,0.6)'}}>Add lunch stop (+30 min)</span>
                </label>
                <div style={{marginBottom:'16px'}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'2px',color:'rgba(255,255,255,0.3)',marginBottom:'6px'}}>MESSAGE</div><textarea value={etaData.message} onChange={function(e){setEtaData(function(d){return Object.assign({},d,{message:e.target.value})})}} style={{width:'100%',padding:'12px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#ffffff',fontSize:'13px',fontFamily:"'Barlow',sans-serif",outline:'none',boxSizing:'border-box',minHeight:'120px',resize:'vertical',lineHeight:1.5}} /></div>
                <div style={{display:'flex',gap:'8px'}}>
                  {fullJob.client_phone && <button disabled={etaData.sending} onClick={function(){sendETA('sms')}} style={{flex:1,padding:'12px',backgroundColor:'#b48c3c',border:'none',borderRadius:'3px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:700,letterSpacing:'2px',cursor:'pointer'}}>{etaData.sending?'...':'SEND SMS'}</button>}
                  {fullJob.client_email && <button disabled={etaData.sending} onClick={function(){sendETA('email')}} style={{flex:1,padding:'12px',backgroundColor:'#4a6a8c',border:'none',borderRadius:'3px',color:'#ffffff',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:700,letterSpacing:'2px',cursor:'pointer'}}>{etaData.sending?'...':'SEND EMAIL'}</button>}
                </div>
              </div>
            )}
          </div>
        </div>}

        {!safeMode && showSnapshot && fullJob.quote_snapshot && (() => {
          var snap = fullJob.quote_snapshot || {}
          var snapBid = snap.bid || {}
          var snapLines = Array.isArray(snapBid.bid_lines) ? snapBid.bid_lines : []
          var snapAt = fullJob.quote_snapshot_at ?? snap.captured_at ?? null
          var signedAt = snap.signed_at ?? snapBid.signed_at ?? null
          var signerName = snap.signer_name ?? snapBid.signer_name ?? null
          var sigData = snap.signature_data ?? snapBid.signature_data ?? null
          var snapTotal = snapBid.total ?? snapBid.bid_total ?? fullJob.contract_value ?? 0
          var bizName = snapBid.business_id === 'pools' ? 'CP Quality Pools' : 'Riverbend Contracting Co.'
          var fmt = function(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
          return (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }} onClick={function() { setShowSnapshot(false) }}>
              <div style={{ width: '100%', maxWidth: '640px', backgroundColor: '#ffffff', borderRadius: '8px', padding: '32px', fontFamily: 'sans-serif', color: '#222' }} onClick={function(e) { e.stopPropagation() }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: '#888', fontWeight: 700 }}>FROZEN QUOTE — AS RECEIVED BY CUSTOMER</div>
                  <button onClick={function() { setShowSnapshot(false) }} style={{ background: 'none', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer' }}>X</button>
                </div>
                <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
                  <h2 style={{ margin: 0, fontSize: '20px', color: '#222' }}>{bizName}</h2>
                  <div style={{ color: '#888', fontSize: '12px', marginTop: '2px' }}>Sent {snapAt ? new Date(snapAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</div>
                </div>
                <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '10px', color: '#999', letterSpacing: '1px', fontWeight: 700, marginBottom: '4px' }}>PREPARED FOR</div>
                    <div style={{ fontSize: '15px', fontWeight: 600, wordBreak: 'break-word' }} title={String(snapBid.client_name || '')}>{truncate(snapBid.client_name, 80) || '—'}</div>
                    {snapBid.site_address && <div style={{ fontSize: '13px', color: '#666', wordBreak: 'break-word' }} title={String(snapBid.site_address)}>{truncate(snapBid.site_address, 120)}</div>}
                    {snapBid.client_phone && <div style={{ fontSize: '13px', color: '#666' }}>{truncate(snapBid.client_phone, 40)}</div>}
                    {snapBid.client_email && <div style={{ fontSize: '13px', color: '#666', wordBreak: 'break-all' }}>{truncate(snapBid.client_email, 80)}</div>}
                  </div>
                  <div style={{ textAlign: 'right', minWidth: 0 }}>
                    <div style={{ fontSize: '10px', color: '#999', letterSpacing: '1px', fontWeight: 700, marginBottom: '4px' }}>QUOTE</div>
                    {snapBid.estimate_number && <div style={{ fontSize: '15px', fontWeight: 600 }}>#{truncate(snapBid.estimate_number, 30)}</div>}
                    {snapBid.job_name && <div style={{ fontSize: '13px', color: '#666', marginTop: '4px', wordBreak: 'break-word' }} title={String(snapBid.job_name)}>{truncate(snapBid.job_name, 80)}</div>}
                  </div>
                </div>
                {snapLines.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', fontSize: '14px' }}>
                    <thead><tr style={{ background: '#f8f8fa' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #eee', fontSize: '11px', color: '#888', letterSpacing: '1px' }}>DESCRIPTION</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '2px solid #eee', fontSize: '11px', color: '#888', letterSpacing: '1px', width: '110px' }}>AMOUNT</th>
                    </tr></thead>
                    <tbody>
                      {snapLines.filter(function(l) { return l && l.category !== 'notes' }).map(function(l, idx) {
                        var amt = l.total_cost != null ? l.total_cost : (l.total_price != null ? l.total_price : null)
                        return (
                          <tr key={l.id || idx}>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', color: '#333' }}>{l.description || ''}</td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', textAlign: 'right', fontWeight: 600 }}>{amt != null ? fmt(amt) : ''}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '14px', borderTop: '3px solid #b48c3c' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '10px', color: '#999', letterSpacing: '1px', fontWeight: 700 }}>TOTAL AT SIGNING</div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: '#b48c3c' }}>{fmt(snapTotal)}</div>
                  </div>
                </div>
                {snapBid.notes && (
                  <div style={{ marginTop: '16px', padding: '12px', background: '#f8f8fa', borderRadius: '6px', fontSize: '13px', color: '#444', whiteSpace: 'pre-wrap' }}>{snapBid.notes}</div>
                )}
                {snapBid.disclaimers && (
                  <div style={{ marginTop: '12px', fontSize: '11px', color: '#999', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{snapBid.disclaimers}</div>
                )}
                {signedAt ? (
                  <div style={{ marginTop: '20px', padding: '16px', background: '#e8f5e9', border: '2px solid #a5d6a7', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#2e7d32' }}>QUOTE SIGNED</div>
                    {signerName && <div style={{ marginTop: '6px', fontSize: '13px', color: '#333' }}>Signed by: <strong>{signerName}</strong></div>}
                    {sigData && <div style={{ marginTop: '8px' }}><img src={sigData} alt="Signature" style={{ maxWidth: '280px', maxHeight: '90px', borderBottom: '1px solid #ccc' }} /></div>}
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>Signed on {new Date(signedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                  </div>
                ) : (
                  <div style={{ marginTop: '20px', padding: '12px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', fontSize: '13px', color: '#7a5d00', textAlign: 'center' }}>Awaiting customer signature.</div>
                )}
                {snap.quote_url && (
                  <div style={{ marginTop: '16px', textAlign: 'center' }}>
                    <a href={snap.quote_url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: '#4a6a8c', textDecoration: 'underline' }}>Open the live quote page →</a>
                  </div>
                )}
              </div>
            </div>
          )
        })()}

        {showLinkBid && (() => {
          var t = (linkBidSearch || '').toLowerCase().trim()
          var filtered = !t ? linkableBids : linkableBids.filter(function(b) {
            return [b.client_name, b.job_name, b.site_address].some(function(f) { return f && String(f).toLowerCase().includes(t) })
          })
          return (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1101, padding: '20px' }} onClick={function() { if (!linkingBid) setShowLinkBid(false) }}>
              <div style={{ width: '100%', maxWidth: '520px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '20px', fontFamily: "'Barlow',sans-serif" }} onClick={function(e) { e.stopPropagation() }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', letterSpacing: '2px', color: '#fff', fontWeight: 700 }}>LINK EXISTING BID</div>
                  <button onClick={function() { setShowLinkBid(false) }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' }}>X</button>
                </div>
                <input
                  autoFocus
                  value={linkBidSearch}
                  onChange={function(e) { setLinkBidSearch(e.target.value) }}
                  placeholder="Search by name, job, or address"
                  style={{ marginBottom: '10px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none' }}
                />
                <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {filtered.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>No unassigned bids match.</div>
                  ) : filtered.map(function(b) {
                    return (
                      <button key={b.id} disabled={linkingBid} onClick={function() { linkExistingBid(b.id) }} style={{ textAlign: 'left', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', color: '#ffffff', cursor: linkingBid ? 'wait' : 'pointer' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600 }} title={String(b.client_name || '')}>{truncate(b.client_name || '(no name)', 60)}</div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginTop: '2px' }} title={String(b.job_name || '')}>{truncate(b.job_name || '', 60)}</div>
                        {b.site_address && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }} title={String(b.site_address)}>{truncate(b.site_address, 60)}</div>}
                        <div style={{ fontSize: '11px', color: '#b48c3c', marginTop: '2px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1px' }}>{b.total != null ? '$' + Number(b.total).toLocaleString('en-US', { maximumFractionDigits: 0 }) : ''} {(b.business_id || '').toUpperCase()} {b.status ? '— ' + b.status.toUpperCase() : ''}</div>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })()}

        {showSyncLog && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1101, padding: '20px' }} onClick={function() { setShowSyncLog(false) }}>
            <div style={{ width: '100%', maxWidth: '480px', maxHeight: '80vh', overflowY: 'auto', backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '24px', fontFamily: "'Barlow',sans-serif" }} onClick={function(e) { e.stopPropagation() }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', letterSpacing: '2px', color: '#fff', fontWeight: 700 }}>SYNC LOG</div>
                <button onClick={function() { setShowSyncLog(false) }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' }}>X</button>
              </div>
              {(Array.isArray(fullJob.sync_log) ? fullJob.sync_log : []).map(function(entry, idx) {
                var color = entry.outcome === 'error' ? '#e05c5c' : entry.outcome === 'synced' ? '#4a8c6a' : 'rgba(255,255,255,0.4)'
                return (
                  <div key={idx} style={{ padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid ' + color, borderRadius: '3px', marginBottom: '6px' }}>
                    <div style={{ fontSize: '12px', color: color, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1.5px', fontWeight: 700 }}>{(entry.outcome || 'unknown').toUpperCase()}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>{entry.at ? new Date(entry.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</div>
                    {entry.message && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', marginTop: '4px' }}>{entry.message}</div>}
                  </div>
                )
              })}
              {(!fullJob.sync_log || fullJob.sync_log.length === 0) && <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '20px 0' }}>No sync history yet.</div>}
            </div>
          </div>
        )}

        {confirmEmail && <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1002,padding:'24px'}} onClick={function(){setConfirmEmail(null)}}>
          <div style={{backgroundColor:'#0f1420',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',padding:'28px',width:'100%',maxWidth:'560px',maxHeight:'85vh',overflow:'auto'}} onClick={function(e){e.stopPropagation()}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,letterSpacing:'2px',color:'#4a8c6a'}}>SEND CONFIRMATION EMAIL</div>
              <button onClick={function(){setConfirmEmail(null)}} style={{background:'none',border:'none',color:'rgba(255,255,255,0.4)',fontSize:'18px',cursor:'pointer'}}>X</button>
            </div>
            <div style={{marginBottom:'10px'}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'2px',color:'rgba(255,255,255,0.3)',marginBottom:'6px'}}>TO</div><div style={{padding:'8px 12px',backgroundColor:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'3px',color:'#ffffff',fontSize:'13px'}}>{confirmEmail.to}</div></div>
            <div style={{marginBottom:'10px'}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'2px',color:'rgba(255,255,255,0.3)',marginBottom:'6px'}}>SUBJECT</div><input value={confirmEmail.subject} onChange={function(e){setConfirmEmail(Object.assign({},confirmEmail,{subject:e.target.value}))}} style={{width:'100%',padding:'10px 12px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#ffffff',fontSize:'13px',fontFamily:"'Barlow',sans-serif",outline:'none',boxSizing:'border-box'}} /></div>
            <div style={{marginBottom:'16px'}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'2px',color:'rgba(255,255,255,0.3)',marginBottom:'6px'}}>MESSAGE</div><textarea value={confirmEmail.body} onChange={function(e){setConfirmEmail(Object.assign({},confirmEmail,{body:e.target.value}))}} style={{width:'100%',padding:'12px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#ffffff',fontSize:'13px',fontFamily:"'Barlow',sans-serif",outline:'none',boxSizing:'border-box',minHeight:'200px',resize:'vertical',lineHeight:1.5}} /></div>
            <div style={{display:'flex',gap:'8px'}}>
              <button onClick={function(){setConfirmEmail(null)}} style={{padding:'12px 20px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.5)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'}}>CANCEL</button>
              <button disabled={emailSending} onClick={sendConfirmEmail} style={{flex:1,padding:'12px',backgroundColor:'#4a8c6a',border:'none',borderRadius:'3px',color:'#ffffff',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:700,letterSpacing:'2px',cursor:'pointer'}}>{emailSending?'SENDING...':'SEND CONFIRMATION'}</button>
            </div>
          </div>
        </div>}
      </div>
    </div>
  )
}

// Reads jobs.* columns the bid trigger keeps in sync. Read-only when the
// job has a linked bid (the trigger owns those columns); editable when
// not (lets crews adjust scope on hand-built jobs without bouncing back
// to the bids tab).
function ScopePanel({ job, onJobUpdated, canEdit }) {
  const linked = !!job.bid_id
  const [editingScope, setEditingScope] = useState(false)
  const [form, setForm] = useState(function() {
    var init = { pool_size: job.pool_size ?? '', pool_install_type: job.pool_install_type ?? '' }
    JOB_SCOPE_COLUMNS.forEach(function(c) { init[c.column] = job[c.column] === true })
    return init
  })
  const [saving, setSaving] = useState(false)

  // Re-sync the local form whenever the parent job row changes (e.g. the
  // 2-hour cron just synced new flags, or the user reopened the panel
  // after another tab edited it).
  useEffect(function() {
    var next = { pool_size: job.pool_size ?? '', pool_install_type: job.pool_install_type ?? '' }
    JOB_SCOPE_COLUMNS.forEach(function(c) { next[c.column] = job[c.column] === true })
    setForm(next)
  }, [job.id, job.pool_size, job.pool_install_type, job.is_tdi, job.in_place_of_old_pool, job.has_deep_end, job.dirt_haul_away, job.water_delivery, job.sand_delivery, job.has_main_drain]) // eslint-disable-line react-hooks/exhaustive-deps

  var trueFlagLabels = JOB_SCOPE_COLUMNS.filter(function(c) { return job[c.column] === true }).map(function(c) { return c.label })
  var installLabel = job.pool_install_type ? (INSTALL_TYPE_LABELS[job.pool_install_type] ?? null) : null
  var hasAny = trueFlagLabels.length > 0 || job.pool_size || installLabel

  var saveScope = async function() {
    setSaving(true)
    try {
      var patch = { pool_size: form.pool_size || null, pool_install_type: form.pool_install_type || null }
      JOB_SCOPE_COLUMNS.forEach(function(c) { patch[c.column] = !!form[c.column] })
      const { error } = await supabase.from('jobs').update(patch).eq('id', job.id)
      if (error) { alert('Could not save scope: ' + error.message); setSaving(false); return }
      if (onJobUpdated) onJobUpdated(patch)
      setEditingScope(false)
    } catch (err) {
      alert('Save failed: ' + (err?.message || 'unknown'))
    }
    setSaving(false)
  }

  return (
    <div style={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={styles.sectionTitle}>{linked ? 'SCOPE (SYNCED FROM BID)' : 'SCOPE'}</div>
        {!linked && canEdit && !editingScope && (
          <button onClick={function() { setEditingScope(true) }} style={{ padding: '4px 10px', backgroundColor: 'transparent', border: '1px solid rgba(180,140,60,0.4)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' }}>EDIT</button>
        )}
      </div>
      {!editingScope ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
            {(job.pool_size || installLabel) && (
              <span style={{ fontSize: '13px', color: '#ffffff', fontWeight: 600, marginRight: '4px' }}>
                {[job.pool_size, installLabel].filter(Boolean).join(' ')}
              </span>
            )}
            {trueFlagLabels.length > 0 && trueFlagLabels.map(function(label) {
              return <span key={label} style={{ fontSize: '10px', lineHeight: 1.4, padding: '2px 6px', borderRadius: '8px', backgroundColor: 'rgba(180,140,60,0.15)', color: '#d2b674', border: '1px solid rgba(180,140,60,0.25)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.5px' }}>{label}</span>
            })}
            {!hasAny && <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>No scope detected.</span>}
          </div>
          {linked && (
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: 1.5 }}>
              These fields are managed by the linked bid. Edit the bid to change scope. Manual overrides are not available on bid-linked jobs.
            </div>
          )}
        </>
      ) : (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
            <div>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}>POOL SIZE</div>
              <input value={form.pool_size} onChange={function(e) { setForm(function(f) { return Object.assign({}, f, { pool_size: e.target.value }) }) }} placeholder="e.g. 18x33 Oval" style={{ width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}>INSTALL TYPE</div>
              <select value={form.pool_install_type} onChange={function(e) { setForm(function(f) { return Object.assign({}, f, { pool_install_type: e.target.value }) }) }} style={{ width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' }}>
                <option value="">—</option>
                {Object.keys(INSTALL_TYPE_LABELS).map(function(k) { return <option key={k} value={k}>{INSTALL_TYPE_LABELS[k]}</option> })}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            {JOB_SCOPE_COLUMNS.map(function(c) {
              var on = !!form[c.column]
              return (
                <label key={c.column} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', backgroundColor: on ? 'rgba(180,140,60,0.12)' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (on ? 'rgba(180,140,60,0.35)' : 'rgba(255,255,255,0.08)'), borderRadius: '3px', cursor: 'pointer', fontSize: '12px', color: on ? '#d2b674' : 'rgba(255,255,255,0.6)' }}>
                  <input type="checkbox" checked={on} onChange={function(e) { var v = e.target.checked; setForm(function(f) { var n = Object.assign({}, f); n[c.column] = v; return n }) }} style={{ accentColor: '#b48c3c' }} />
                  <span style={{ fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1px' }}>{c.label.toUpperCase()}</span>
                </label>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button disabled={saving} onClick={function() { setEditingScope(false) }} style={{ padding: '6px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.55)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' }}>CANCEL</button>
            <button disabled={saving} onClick={saveScope} style={{ padding: '6px 14px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' }}>{saving ? 'SAVING…' : 'SAVE SCOPE'}</button>
          </div>
        </div>
      )}
    </div>
  )
}

function PaymentModal({ job, onClose, onSuccess }) {
  const [method, setMethod] = useState('check')
  const [checkNumber, setCheckNumber] = useState('')
  const [sending, setSending] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const handleCollect = async () => {
    setSending(true)
    setError('')
    try {
      const clientPhone = job.client_phone?.replace(/\D/g, '')
      if (!clientPhone || clientPhone.length < 10) { setError('No valid client phone number on file.'); setSending(false); return }
      const bizName = job.business_id === 'pools' ? 'CP Quality Pools' : 'Riverbend Clearing Co.'
      const bizPhone = job.business_id === 'pools' ? '(586) 991-3260' : '(810) 923-6229'
      const receiptMsg = 'Hi ' + job.client_name + ', your payment of $' + Number(job.contract_value).toLocaleString() + ' has been received for ' + (job.title || job.client_name) + ' at ' + job.site_address + '. Thank you for choosing ' + bizName + '! Questions? Call us at ' + bizPhone + '.'
      const response = await fetch('/api/send-sms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: '+1' + clientPhone, message: receiptMsg, business_id: job.business_id }) })
      const data = await response.json()
      if (data.error) { setError(data.error); setSending(false); return }
      await supabase.from('jobs').update({ notes: (job.notes || '') + '\nPayment collected. Method: ' + method + (checkNumber ? ', Check #' + checkNumber : '') + '. Receipt texted.' }).eq('id', job.id)
      setMessage('Payment collected and receipt sent!')
      setTimeout(() => onSuccess(), 1500)
    } catch (err) { setError(err.message) }
    setSending(false)
  }

  return (
    <div style={styles.paymentOverlay} onClick={onClose}>
      <div style={styles.paymentCard} onClick={e => e.stopPropagation()}>
        <div style={styles.paymentTitle}>COLLECT PAYMENT</div>
        <div style={styles.paymentAmount}>${Number(job.contract_value || 0).toLocaleString()}</div>
        <div style={styles.paymentClient}>{job.client_name}</div>
        <div style={styles.methodRow}>
          {['check', 'cash'].map(m => <button key={m} onClick={() => setMethod(m)} style={{ ...styles.methodBtn, ...(method === m ? styles.methodBtnActive : {}) }}>{m.toUpperCase()}</button>)}
        </div>
        {method === 'check' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>CHECK NUMBER</label>
            <input value={checkNumber} onChange={e => setCheckNumber(e.target.value)} style={styles.input} placeholder="e.g. 1042" />
          </div>
        )}
        {error && <div style={styles.errorMsg}>{error}</div>}
        {message && <div style={styles.successMsg}>{message}</div>}
        <div style={styles.paymentNote}>Receipt will be texted to {job.client_phone || 'client phone on file'}</div>
        <div style={styles.paymentActions}>
          <button onClick={onClose} style={styles.cancelBtn}>CANCEL</button>
          <button onClick={handleCollect} disabled={sending} style={styles.collectConfirmBtn}>{sending ? 'SENDING...' : 'CONFIRM + SEND RECEIPT'}</button>
        </div>
      </div>
    </div>
  )
}

const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 400, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '20px' },
  card: { width: '480px', maxWidth: '100%', height: 'calc(100vh - 40px)', backgroundColor: '#0d1420', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', display: 'flex', flexDirection: 'column', overflowY: 'auto', fontFamily: "'Barlow',sans-serif" },
  cardTop: { padding: '24px 24px 0' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  jobNumber: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' },
  jobTitle: { fontSize: '20px', fontWeight: 600, color: '#ffffff', marginBottom: '6px' },
  bizLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px' },
  headerRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' },
  statusBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1.5px', padding: '4px 10px', borderRadius: '3px', border: '1px solid' },
  editBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1.5px', padding: '5px 10px', cursor: 'pointer' },
  closeBtn: { backgroundColor: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer', padding: '4px' },
  cardBody: { flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '4px' },
  loadingText: { color: 'rgba(255,255,255,0.3)', fontSize: '15px', textAlign: 'center', padding: '40px 0' },
  editForm: { display: 'flex', flexDirection: 'column', gap: '16px' },
  editGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)' },
  input: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '10px 12px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow',sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' },
  editActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '8px' },
  cancelBtn: { padding: '10px 18px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '2px', cursor: 'pointer' },
  saveBtn: { padding: '10px 20px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  section: { marginBottom: '20px' },
  sectionTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '10px' },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  infoItem: {},
  infoLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.25)', marginBottom: '3px' },
  infoValue: { fontSize: '15px', color: '#ffffff' },
  // WORK SCOPE — hero section, bigger than other blocks so the crew sees it first
  scopeSection: { marginBottom: '24px', padding: '16px 18px', backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.25)', borderRadius: '4px' },
  scopeHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid rgba(180,140,60,0.25)', gap: '12px', flexWrap: 'wrap' },
  scopeTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '15px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c' },
  scopeTotal: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '20px', fontWeight: 700, color: '#ffffff' },
  scopeGroup: { marginBottom: '14px' },
  scopeGroupTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.45)', marginBottom: '8px' },
  scopeTable: { display: 'flex', flexDirection: 'column', gap: '2px' },
  scopeRow: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 60px 90px', gap: '10px', padding: '7px 10px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: '3px', alignItems: 'center' },
  scopeRowDesc: { fontSize: '14px', color: '#ffffff', lineHeight: 1.4, minWidth: 0 },
  scopeRowQty: { fontSize: '13px', color: 'rgba(255,255,255,0.55)', textAlign: 'right' },
  scopeRowPrice: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 600, color: '#b48c3c', textAlign: 'right' },
  scopeGroupTotal: { marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed rgba(255,255,255,0.1)', textAlign: 'right', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', color: 'rgba(255,255,255,0.55)' },
  scopeNotes: { marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)' },
  scopeNotesBody: { fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginTop: '4px' },
  scopeEmpty: { padding: '14px', fontSize: '13px', color: 'rgba(255,255,255,0.4)', textAlign: 'center' },
  scopeEditBtn: { marginTop: '10px', padding: '8px 14px', backgroundColor: 'rgba(180,140,60,0.2)', border: '1px solid rgba(180,140,60,0.4)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  // Sent Quote snapshot card — shown above WORK SCOPE
  snapshotCard: { marginBottom: '16px', padding: '14px 16px', backgroundColor: 'rgba(74,106,140,0.08)', border: '1px solid rgba(74,106,140,0.3)', borderRadius: '4px' },
  snapshotHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' },
  snapshotTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '2px', color: '#4a6a8c', fontWeight: 700 },
  snapshotDate: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' },
  snapshotTotal: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '18px', fontWeight: 700, color: '#ffffff' },
  snapshotSigned: { marginTop: '8px', fontSize: '13px', color: '#4a8c6a', fontWeight: 600 },
  snapshotPending: { marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
  snapshotActions: { marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' },
  snapshotViewBtn: { padding: '8px 14px', backgroundColor: 'rgba(74,106,140,0.2)', border: '1px solid rgba(74,106,140,0.4)', borderRadius: '3px', color: '#9bb6d4', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  snapshotWarn: { marginTop: '10px', padding: '8px 10px', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', fontSize: '12px', color: '#b48c3c', lineHeight: 1.4 },
  // Bid sync indicator + manual controls
  syncBox: { marginBottom: '16px', padding: '12px 14px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' },
  syncHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  syncLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.55)', fontWeight: 700 },
  syncToggleBtn: { display: 'flex', alignItems: 'center', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 },
  syncMeta: { marginTop: '6px', fontSize: '11px', color: 'rgba(255,255,255,0.4)' },
  syncActions: { marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' },
  syncNowBtn: { padding: '7px 12px', backgroundColor: 'rgba(74,140,106,0.18)', border: '1px solid rgba(74,140,106,0.4)', borderRadius: '3px', color: '#4a8c6a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  syncLogBtn: { padding: '7px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  // Completion panel
  completedPanel: { marginBottom: '20px', padding: '18px 20px', backgroundColor: 'rgba(74,140,106,0.08)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '4px' },
  completedPanelLite: { marginBottom: '20px', padding: '14px 18px', backgroundColor: 'rgba(74,140,106,0.06)', border: '1px solid rgba(74,140,106,0.2)', borderRadius: '4px', textAlign: 'center' },
  completedTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', color: '#4a8c6a', marginBottom: '6px' },
  completedDate: { fontSize: '12px', color: 'rgba(255,255,255,0.5)' },
  cancelledPanel: { marginBottom: '20px', padding: '18px 20px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '4px' },
  cancelledTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.55)', marginBottom: '6px' },
  cancelledNote: { fontSize: '12px', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 },
  cancelledHint: { fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', marginTop: '8px' },
  collectedLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginTop: '8px', marginBottom: '4px' },
  collectedAmount: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '32px', fontWeight: 700, color: '#b48c3c', lineHeight: 1.1 },
  varianceLine: { fontSize: '12px', marginTop: '6px', fontWeight: 600 },
  paymentMetaRow: { display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)' },
  paymentMetaLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', fontWeight: 700, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', marginRight: '6px' },
  paymentMetaValue: { fontSize: '13px', color: '#ffffff' },
  paymentMethodLine: { fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginTop: '6px' },
  paymentNotesBlock: { marginTop: '10px' },
  paymentNotesText: { fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, whiteSpace: 'pre-wrap', marginTop: '4px' },
  editCollectedBtn: { marginTop: '12px', padding: '6px 12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  completeBtn: { width: '100%', padding: '14px', backgroundColor: '#4a8c6a', border: 'none', borderRadius: '4px', color: '#ffffff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer', marginTop: '10px' },
  addressBtn: { backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '14px', width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  addressText: { fontSize: '15px', color: '#ffffff', lineHeight: 1.5 },
  mapsTag: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1.5px', color: '#b48c3c', flexShrink: 0, marginLeft: '12px' },
  contractValue: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '36px', fontWeight: 700, color: '#b48c3c', marginBottom: '6px' },
  paymentStatus: { fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginBottom: '16px' },
  collectBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '14px 20px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer', width: '100%' },
  notes: { fontSize: '15px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, backgroundColor: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '3px' },
  statusBtns: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  statusBtn: { padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '1px', cursor: 'pointer' },
  paymentOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  paymentCard: { width: '400px', maxWidth: '100%', backgroundColor: '#0d1420', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '4px', padding: '32px', fontFamily: "'Barlow',sans-serif" },
  paymentTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', letterSpacing: '3px', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' },
  paymentAmount: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '48px', fontWeight: 700, color: '#b48c3c', lineHeight: 1, marginBottom: '4px' },
  paymentClient: { fontSize: '16px', color: 'rgba(255,255,255,0.6)', marginBottom: '24px' },
  methodRow: { display: 'flex', gap: '8px', marginBottom: '20px' },
  methodBtn: { flex: 1, padding: '12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 600, letterSpacing: '2px', cursor: 'pointer' },
  methodBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  errorMsg: { backgroundColor: 'rgba(220,60,60,0.1)', border: '1px solid rgba(220,60,60,0.3)', borderRadius: '3px', padding: '10px 14px', color: '#ff6b6b', fontSize: '14px', marginBottom: '16px' },
  successMsg: { backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', padding: '10px 14px', color: '#4a8c6a', fontSize: '14px', marginBottom: '16px' },
  paymentNote: { fontSize: '13px', color: 'rgba(255,255,255,0.3)', marginBottom: '20px', fontStyle: 'italic' },
  paymentActions: { display: 'flex', gap: '12px' },
  collectConfirmBtn: { flex: 2, padding: '14px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  checkGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '16px' },
  checkbox: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', cursor: 'pointer', fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
  checkboxActive: { backgroundColor: 'rgba(180,140,60,0.12)', border: '1px solid rgba(180,140,60,0.35)', color: '#ffffff' },
  checkDot: { width: '10px', height: '10px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 },
  checkDotActive: { backgroundColor: '#b48c3c', border: '1px solid #b48c3c' },
}
